// 批次 1（config 模組）：逐 resource 的業務規則層——種子列補業務欄位、寫入驗證、寫入後副作用。
//
// 為什麼放在 mocks：這些規則在 legacy 是後端 controller／前端 JSP 的工作
// （例：AnnualController 的預設年度唯一性、role.jsp:184-193 的角色重複檢查、
// annual.jsp:29 的 `row.erasable==1 && row.isEnable==1` 刪除條件）。
// 雛形沒有後端，規則由 mock 資料層承接，行為才會跟著 DataSource 走
// （scenario 切換與 localStorage 持久化都一致）；頁面模板只負責把規則「顯示」出來。
//
// 【證據等級】§7 第 3 級（原始碼）。種子值（年度清單、usage 旗標等）是 legacy
// 資料庫內容，雛形以固定種子呈現——【雛形值待實機校準】。
//
// 本檔刻意**不 import** `mock-data-source.ts`（它會 import 本檔，避免循環相依）：
// 驗證規則回傳描述，由 MockDataSource 自己 throw `MockDataError`。

import type { Row } from './types';
import { legacyContractFieldsFor, optionsFor } from './legacy-fields';

// ---------------------------------------------------------------------------
// 種子列補欄（enrich）
// ---------------------------------------------------------------------------

/**
 * config-annual 種子配方（逐 index 套用，其餘列維持 fixtures 泛型值）。
 * - `date`＝年度欄位（legacy `year`：115 民國／2026 西元混用，annual.jsp 表格 render 直出）。
 * - `status`＝是否預設欄位（legacy `isDefault`：是／否，modal 用 radio 是/否，annual.jsp:450-451）。
 * - `status2`＝狀態欄位（legacy `isEnable`：啟用／停用）。
 * - `erasable`：annual.jsp:29 刪除連結條件 `row.erasable==1 && row.isEnable==1`。
 * - `usageUsed`：`config/checkAnnualUsageAjax` 的 `response.isUsageCount`
 *   （annual.jsp:318-330：已被使用 → 不能刪除、停用要加碼確認）。
 */
const ANNUAL_SEED: readonly Row[] = [
  { date: '2026', status: '是', score: 0, status2: '啟用', erasable: '0', usageUsed: '1' },
  { date: '115', status: '否', score: 1, status2: '啟用', erasable: '1', usageUsed: '1' },
  { date: '2025', status: '否', score: 2, status2: '啟用', erasable: '1', usageUsed: '0' },
  { date: '114', status: '否', score: 3, status2: '停用', erasable: '1', usageUsed: '0' },
];

/**
 * config-role 種子配方。角色名稱（category）逐字來自 legacy 實測 rowActions 標籤
 * （contract `config-role` rowActions：系統管理員／教學管理單位／科部管理員／授課老師／
 * 學員／導師／臨床教師／計畫主持人／單位主管）。role.jsp:32-45：`row.id>=1000` 才有刪除
 * 連結——系統角色不可刪，雛形以 `deletable` 旗標表達。
 * - `sysRole`／`flowRole`＝系統控制標記／流程控制標記（legacy 從 enumConfig DB 讀，
 *   RoleController.java:58-59；雛形給代表值）。
 * - `permissionIds`＝該角色勾選的權限 id（role.jsp setRolePermission：字串陣列）。
 */
const ROLE_SYSTEM_NAMES: readonly string[] = [
  '系統管理員',
  '教學管理單位',
  '科部管理員',
  '授課老師',
  '學員',
  '導師',
  '臨床教師',
  '計畫主持人',
  '單位主管',
];

const ROLE_CUSTOM_SEED: readonly Row[] = [
  { category: '教學檢討會主席', score: 10, sysRole: '', flowRole: '', deletable: '1' },
  { category: '住宿醫師導生', score: 11, sysRole: '', flowRole: '', deletable: '1' },
];

/**
 * config-location 種子補欄：`usageUsed`（location.jsp changeLocationStatus：
 * `config/location/checkLocationAjax` 的 `response.isUsed`，已被使用 → 不能刪除、
 * 停用要加碼確認）。
 */

/**
 * config-department 種子配方（index 0-7 套用，其餘列維持泛型值）。
 * - 前四列一級部門（parentId='0'），後四列掛在前四列下的子部門（department.jsp 樹狀圖由
 *   parentId 組樹，:198-225）；`unit`（部門名稱）逐字用 legacy 泛型語意池一致的科部名。
 * - `id`（部門代碼欄）沿用 fixtures 的列鍵（CON-114-00xx），樹的父子引用同一個字串。
 * - `erasable`：department.jsp:526 刪除條件 `isPermitDelete && row.erasable==1 && row.isEnable==1`。
 * - `usageUsed`：department.jsp:451-460 `config/checkDepartmentUsageAjax` 的 isUsageCount。
 * - `shortName`：部門簡稱（modal 欄位，department.jsp:717-723）。
 * - 批次 12（B1-4）：`organizationId`＝醫院（修改一級部門回填＋disabled，department.jsp:89-90；
 *   新增子部門由父列繼承 :62），取醫院下拉第一個選項值【雛形值待實機校準】；
 *   `updateUserName`/`updateDate`（＋`updateDateTime`，列表路徑 :184 讀後者、樹路徑 :125 讀前者）
 *   ＝異動人員/日期（modal updateInfoDiv，:732-741）【雛形值待實機校準】。
 */
const DEPARTMENT_ORGANIZATION_SEED = (() => {
  const field = legacyContractFieldsFor('config-department')?.find(
    (candidate) => candidate.name === 'organizationId',
  );
  return field === undefined ? '' : (optionsFor('config-department', field)[0]?.value ?? '');
})();

const DEPARTMENT_SEED: readonly Row[] = [
  { unit: '內科部', parentId: '0', shortName: '內科', erasable: '1', usageUsed: '1', status: '啟用', score: 1, organizationId: DEPARTMENT_ORGANIZATION_SEED, updateUserName: '系統管理員', updateDate: '2026/01/05', updateDateTime: '2026/01/05' },
  { unit: '外科部', parentId: '0', shortName: '外科', erasable: '1', usageUsed: '0', status: '啟用', score: 2, organizationId: DEPARTMENT_ORGANIZATION_SEED, updateUserName: '系統管理員', updateDate: '2026/01/05', updateDateTime: '2026/01/05' },
  { unit: '家庭醫學部', parentId: '0', shortName: '家醫', erasable: '1', usageUsed: '0', status: '啟用', score: 3, organizationId: DEPARTMENT_ORGANIZATION_SEED, updateUserName: '系統管理員', updateDate: '2026/01/05', updateDateTime: '2026/01/05' },
  { unit: '急診醫學部', parentId: '0', shortName: '急診', erasable: '0', usageUsed: '0', status: '啟用', score: 4, organizationId: DEPARTMENT_ORGANIZATION_SEED, updateUserName: '系統管理員', updateDate: '2026/01/05', updateDateTime: '2026/01/05' },
  { unit: '心臟內科', parentId: 'DEPT-0', shortName: '心內', erasable: '1', usageUsed: '0', status: '啟用', score: 5, organizationId: DEPARTMENT_ORGANIZATION_SEED, updateUserName: '系統管理員', updateDate: '2026/01/05', updateDateTime: '2026/01/05' },
  { unit: '胃腸內科', parentId: 'DEPT-0', shortName: '胃腸', erasable: '1', usageUsed: '0', status: '啟用', score: 6, organizationId: DEPARTMENT_ORGANIZATION_SEED, updateUserName: '系統管理員', updateDate: '2026/01/05', updateDateTime: '2026/01/05' },
  { unit: '一般外科', parentId: 'DEPT-1', shortName: '一般外', erasable: '1', usageUsed: '0', status: '啟用', score: 7, organizationId: DEPARTMENT_ORGANIZATION_SEED, updateUserName: '系統管理員', updateDate: '2026/01/05', updateDateTime: '2026/01/05' },
  { unit: '急診教學科', parentId: 'DEPT-3', shortName: '急教', erasable: '1', usageUsed: '0', status: '停用', score: 8, organizationId: DEPARTMENT_ORGANIZATION_SEED, updateUserName: '系統管理員', updateDate: '2026/01/05', updateDateTime: '2026/01/05' },
  { unit: '整形外科', parentId: 'DEPT-1', shortName: '整形', erasable: '1', usageUsed: '0', status: '啟用', score: 9, organizationId: DEPARTMENT_ORGANIZATION_SEED, updateUserName: '系統管理員', updateDate: '2026/01/05', updateDateTime: '2026/01/05' },
  { unit: '社區醫學科', parentId: 'DEPT-2', shortName: '社區', erasable: '1', usageUsed: '0', status: '啟用', score: 10, organizationId: DEPARTMENT_ORGANIZATION_SEED, updateUserName: '系統管理員', updateDate: '2026/01/05', updateDateTime: '2026/01/05' },
  { unit: '胸腔內科', parentId: 'DEPT-0', shortName: '胸腔', erasable: '1', usageUsed: '0', status: '啟用', score: 11, organizationId: DEPARTMENT_ORGANIZATION_SEED, updateUserName: '系統管理員', updateDate: '2026/01/05', updateDateTime: '2026/01/05' },
  { unit: '毒物科', parentId: 'DEPT-3', shortName: '毒物', erasable: '1', usageUsed: '0', status: '啟用', score: 12, organizationId: DEPARTMENT_ORGANIZATION_SEED, updateUserName: '系統管理員', updateDate: '2026/01/05', updateDateTime: '2026/01/05' },
];

/**
 * course-course-type 種子配方（課程類別維護，courseType.jsp）。
 * - `category`＝訓練類別（legacy jobTypeName）、`category2`＝類型（kindDisplay）、
 *   `category3`＝所屬類別（parentName，指向同批列的 category4 名）、`category4`＝課程類別名稱。
 * - 刪除/啟停條件（courseType.jsp:20-27/:82-90）：`isPermitDelete && row.erasable==1 &&
 *   row.isEnable==1`；雛形不畫權限差異，以 erasable/status 表達。
 * - `usageUsed`：courseType.jsp:347-363 `course/checkCourseTypeUsageAjax` 的 isUsageCount。
 * 【雛形值待實機校準】
 */
const COURSE_TYPE_SEED: readonly Row[] = [
  { category: '西醫UGY', category2: '必修', category3: '', category4: '急救訓練', score: 1, status: '啟用', erasable: '1', usageUsed: '0' },
  { category: '西醫UGY', category2: '必修', category3: '急救訓練', category4: '高級心臟救命術', score: 2, status: '啟用', erasable: '1', usageUsed: '1' },
  { category: '西醫UGY', category2: '選修', category3: '', category4: '醫學倫理', score: 3, status: '啟用', erasable: '1', usageUsed: '0' },
  { category: 'PGY', category2: '必修', category3: '', category4: '感染管制', score: 4, status: '停用', erasable: '1', usageUsed: '0' },
];

/**
 * course-course-manage 種子配方（課程樣版維護，courseManage.jsp）。
 * - `category`＝類型、`category2`＝訓練類別、`unit`＝部門、`category3`＝課程類別、
 *   `title`＝課程樣版名稱（.showEdit 修改連結，:68）。
 * - 刪除/啟停條件：刪除＝erasable==1 && isEnable==1（:41）、切換＝erasable==1（:79-86）。
 * - `usageUsed`：courseManage.jsp checkCourseDeleteUsage 的 isUsageCount（:582-590）。
 * 【雛形值待實機校準】
 */
/**
 * form-class-form-list／form-plan-form-list 種子補欄：`done`（表單是否已完成，
 * js-my/formList.js doUpdateFormStatus/doDeleteForm 的計數條件與 reopen/inform 的擋檔條件）。
 * 【雛形值待實機校準】
 */
const FORM_LIST_SEED: readonly Row[] = [
  { done: '0', enabled: '1' },
  { done: '1', enabled: '1' },
  { done: '0', enabled: '1' },
  { done: '1', enabled: '1' },
];

const QUESTION_LEVEL_SEED: readonly Row[] = [
  { questionType: '單選題', level: '簡單', enableStatus: '啟用' },
  { questionType: '是非題', level: '簡單', enableStatus: '啟用' },
  { questionType: '問答題', level: '中等', enableStatus: '停用' },
  { questionType: '單選題', level: '困難', enableStatus: '啟用' },
];

/**
 * exam 模組種子配方（批次 3）。
 * - exam-question-bank（題庫管理）：`erasable`/`cnt`（題數，刪除確認文字條件 questionBank.jsp:365-368）、
 *   `status`（啟停切換連結在狀態欄，:86-99）。
 * 【雛形值待實機校準】
 */
/**
 * leave-leave-type 種子補欄：`isEnable`（啟停，操作欄顯示與切換連結，leaveType.jsp:53-66）、
 * `erasable`（刪除條件 :16-24：erasable==1 && isEnable==1）。【雛形值待實機校準】
 */
const LEAVE_TYPE_SEED: readonly Row[] = [
  { isEnable: '啟用', erasable: '1' },
  { isEnable: '啟用', erasable: '1' },
  { isEnable: '啟用', erasable: '0' },
  { isEnable: '停用', erasable: '1' },
];

/**
 * leave-leave-form（假單作業，origin leaveForm.jsp；leave-fix 2026-10-04）：
 * - `LEAVE_FORM_STATUS`＝legacy `constant.leaveFormStatus`（0=審核中／1=通過／-1=未通過；
 *   leaveForm.js 清單 status render ＋ leaveForm.jsp 查詢 select 的選項文字）。
 * - `LEAVE_TYPE_OPTIONS`＝請假 modal 假別下拉（getLeaveTypeListAjax；student 擷取
 *   `.tmp/ui/raw-dom/legacy-modals/student/leave-form/leave-leave-form__add.html`：
 *   9001 必修／9002 選修／9003 專科訓練／9004 實地操作／9005 個案討論／9006 院內講習，
 *   與 seed §C LeaveTypeFlow×6 同源；首項「請選擇」由 LeaveFormDialogModal 的 placeholder 補）。
 * - `LEAVE_FORM_SEED`＝12 列表單狀態輪替（zzseed-leave 9201~9212 混合狀態的 mock 鏡像；
 *   值＝LEAVE_FORM_STATUS 的顯示值，非 codepoint）。
 * 【雛形值待實機校準】
 */
export const LEAVE_FORM_STATUS: readonly string[] = ['審核中', '通過', '未通過'];

export const LEAVE_TYPE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '9001', label: '必修' },
  { value: '9002', label: '選修' },
  { value: '9003', label: '專科訓練' },
  { value: '9004', label: '實地操作' },
  { value: '9005', label: '個案討論' },
  { value: '9006', label: '院內講習' },
];

const LEAVE_FORM_SEED: readonly Row[] = [
  { status: '審核中' },
  { status: '通過' },
  { status: '未通過' },
  { status: '審核中' },
  { status: '通過' },
  { status: '審核中' },
  { status: '未通過' },
  { status: '審核中' },
  { status: '通過' },
  { status: '審核中' },
  { status: '未通過' },
  { status: '通過' },
];

/**
 * 批次 8 種子補欄：
 * - discuss-discuss-board：`status`（公開/隱藏，狀態欄 discussBoard.jsp:86-93）、`erasable`/`hasUsage`
 *   （刪除條件 :58-64：erasable==1 && isEnable==1 && !hasUsage）。
 * - discuss-discuss-type：`erasable`（刪除條件）＋狀態欄啟停（leaveType 同款）。
 * 【雛形值待實機校準】
 */
const DISCUSS_TYPE_SEED: readonly Row[] = [
  { status: '啟用', erasable: '1', hasUsage: '0', isParentEnable: '1', usageUsed: '0' },
  { status: '啟用', erasable: '1', hasUsage: '1', isParentEnable: '1', usageUsed: '1' },
  { status: '啟用', erasable: '0', hasUsage: '0', isParentEnable: '1', usageUsed: '0' },
  { status: '停用', erasable: '1', hasUsage: '0', isParentEnable: '0', usageUsed: '0' },
];
const DISCUSS_BOARD_SEED: readonly Row[] = [
  { status: '公開', isEnable: '啟用', erasable: '1', hasUsage: '1' },
  { status: '公開', isEnable: '啟用', erasable: '1', hasUsage: '0' },
  { status: '隱藏', isEnable: '啟用', erasable: '1', hasUsage: '0' },
  { status: '公開', isEnable: '啟用', erasable: '0', hasUsage: '0' },
  { status: '公開', isEnable: '停用', erasable: '1', hasUsage: '0' },
];

const QUESTION_BANK_SEED: readonly Row[] = [
  { unit: '教學部', title: '急救訓練題庫', score: 12, date: '2026/01/05', person: '教師甲', score2: 1, status: '啟用', erasable: '1', cnt: 3 },
  { unit: '內科部', title: '感染管制題庫', score: 0, date: '2026/01/05', person: '教師乙', score2: 2, status: '啟用', erasable: '1', cnt: 0 },
  { unit: '外科部', title: '醫學倫理題庫', score: 8, date: '2026/01/05', person: '教師丙', score2: 3, status: '啟用', erasable: '0', cnt: 2 },
  { unit: '教學部', title: '病歷寫作題庫', score: 5, date: '2026/01/05', person: '教師甲', score2: 4, status: '停用', erasable: '1', cnt: 1 },
];

// ---------------------------------------------------------------------------
// 批次 13（B3-1/B3-3，Gitea issue #8）：exam modal 群種子（ExamEditModal／DispatchExamBody 同源）。
// 【雛形值待實機校準】legacy 由 getExamListAjax/getQuestionBankListAjax/getQuestionListAjax/
// getExamTargetDispatchListAjax 等端點填充（examManage.jsp:285-321、dispatchExam.jsp:327-350），
// 雛形以種子平列；題目池（EXAM_QUESTION_POOL）對應題庫管理 fixtures 的四個題庫列鍵。
// ---------------------------------------------------------------------------

/** 題庫內題目（exam/question/{qBankId}/getQuestionListAjax 的 list 鏡像，examManage.jsp:814-846）。 */
export interface ExamQuestionPoolEntry {
  readonly questionId: string;
  /** QuestionType.java:2-6：YN(1,是非題)/SELECT(2,單選題)/MULTISEL(3,多選題)/QA(4,問答題)/SQA(5,臨床技能測驗題)。 */
  readonly questionType: string;
  readonly content: string;
  readonly answer: string;
  /** QuestionDifficulty.java:3-6：EASY(1)/MODERATE(5)/HARD(10)。 */
  readonly level: string;
}

/** 題庫列鍵（fixtures buildId('exam-question-bank')）→ 題目池。【雛形值待實機校準】 */
export const EXAM_QUESTION_POOL: Readonly<Record<string, readonly ExamQuestionPoolEntry[]>> = {
  'EXA-114-0001': [
    { questionId: 'q-101', questionType: '2', content: 'CPR 胸外按壓的深度應為多少？', answer: '5 至 6 公分', level: '1' },
    { questionId: 'q-102', questionType: '1', content: 'AED 使用前需確認患者無意識且無呼吸。', answer: '是', level: '1' },
    { questionId: 'q-103', questionType: '4', content: '簡述哈姆立克法的操作步驟。', answer: '環繞腹部快速向上向內擠壓', level: '5' },
  ],
  'EXA-114-0002': [
    { questionId: 'q-201', questionType: '2', content: '酒精性乾洗手可殺滅困難梭狀桿菌孢子嗎？', answer: '不可', level: '5' },
    { questionId: 'q-202', questionType: '1', content: '接觸病人前後均應執行手部衛生。', answer: '是', level: '1' },
    { questionId: 'q-203', questionType: '5', content: '示範外科口罩的正確穿脫順序。', answer: '實地操作評分', level: '10' },
  ],
  'EXA-114-0003': [
    { questionId: 'q-301', questionType: '2', content: '知情同意的四大要素為何？', answer: '揭露、理解、自願性、能力', level: '5' },
    { questionId: 'q-302', questionType: '3', content: '下列哪些屬於病人自主權利法保障的範疇？', answer: '預立醫療決定、知情選擇、拒絕醫療', level: '10' },
    { questionId: 'q-303', questionType: '4', content: '舉例說明利益衝突的處理原則。', answer: '申報迴避', level: '5' },
  ],
  'EXA-114-0004': [
    { questionId: 'q-401', questionType: '1', content: '病歷記錄應以中文書寫為原則。', answer: '是', level: '1' },
    { questionId: 'q-402', questionType: '2', content: 'SOAP 記錄中的 S 代表什麼？', answer: 'Subjective（主觀資料）', level: '1' },
    { questionId: 'q-403', questionType: '4', content: '手術同意書應記載哪些必要事項？', answer: '手術風險、替代方案、主持人', level: '10' },
  ],
};

/**
 * 題組（examGroup）與題目明細（examDetail）：存試卷列上（course-clazz-list 子清單先例）。
 * details 欄位照 examManage.jsp q_columns（:65-180）：題庫 examName/題型 questionType/
 * 題目 content/答案 answer/難度 level/配分 score/排序 sort；id 為雛形列鍵。
 */
export interface ExamGroupDetail {
  readonly id: string;
  readonly questionId: string;
  readonly sort: string;
  readonly score: string;
  readonly bankName: string;
  readonly questionType: string;
  readonly content: string;
  readonly answer: string;
  readonly level: string;
}

/** 題組（getExamGroupListAjax 的 list 鏡像，examManage.jsp:558-574）。 */
export interface ExamGroup {
  readonly id: string | null;
  readonly name: string;
  readonly sort: string;
  /** examManage.jsp:1250-1256：1=從題庫選題、2=隨機出題。 */
  readonly examType: string;
  readonly details: readonly ExamGroupDetail[];
}

/** 測試目標下拉（dispatchExam.jsp:46-51 setExamTargetElement；雛形種子平列【雛形值待實機校準】）。 */
export const EXAM_TARGET_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'et-1', label: '一般醫學訓練計畫第一階段測驗' },
  { value: 'et-2', label: '一般醫學訓練計畫第二階段測驗' },
  { value: 'et-3', label: '住院醫師年度測驗' },
];

/** 閱卷負責人（dispatchExam.jsp:174 setAnyAccountList；雛形種子平列【雛形值待實機校準】）。 */
export const EXAM_DISPATCH_TEACHER_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 't-1', label: '教師甲' },
  { value: 't-2', label: '教師乙' },
  { value: 't-3', label: '教師丙' },
];

// ---------------------------------------------------------------------------
// 批次 14（Gitea issue #22）：report 模組查詢表單選項池。
// static 選項照 legacy raw DOM 契約（legacy-fields.generated 的 options.values/texts）；
// dynamic（ajax 填充）照批次 10/11 先例以種子池平列【雛形值待實機校準】。
// ---------------------------------------------------------------------------

/** 類型（kindList；契約 static：全部/課程/會議；select :198-203，enumKey :201）。 */
export const REPORT_KIND_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '', label: '全部' },
  { value: 'class', label: '課程' },
  { value: 'meeting', label: '會議' },
];

/** 類型（courseFormFinishrate.jsp 的 kindList 捕捉只有會議；JSP 靜態有全部）。 */
export const REPORT_KIND_FINISHRATE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '', label: '全部' },
  { value: 'meeting', label: '會議' },
];

/** 訓練階段類型（JSP 靜態；gpaFormReport.jsp:71-79 無外訓醫院、planFormExport.jsp:251-260 有）。 */
export const REPORT_PHASE_TYPE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '', label: '全部' },
  { value: 'Plan', label: '計劃' },
  { value: 'Group', label: '組別' },
  { value: 'Phase', label: '階段' },
  { value: 'SubPhase', label: '子階段' },
  { value: 'RefElective1', label: '選修1' },
  { value: 'RefElective2', label: '選修2' },
];

/** 訓練階段類型（planFormExport 版：多「外訓醫院」community）。 */
export const REPORT_PHASE_TYPE_PLAN_OPTIONS: readonly { value: string; label: string }[] = [
  ...REPORT_PHASE_TYPE_OPTIONS,
  { value: 'community', label: '外訓醫院' },
];

/** 訓練類別（JSP 靜態單選；gpaFormReport.jsp:52 西醫UGY selected value=1）。 */
export const REPORT_JOB_TYPE_UGY: readonly { value: string; label: string }[] = [
  { value: '1', label: '西醫UGY' },
];

/** 訓練類別（JSP 靜態單選；allStudentScores.jsp:52 西醫PGY selected value=2）。 */
export const REPORT_JOB_TYPE_PGY: readonly { value: string; label: string }[] = [
  { value: '2', label: '西醫PGY' },
];

/** 訓練類別（JSP 靜態；courseFormFinishrate.jsp:42-45 西醫UGY 1/中醫UGY 222/牙醫PGY 223＋請選擇 placeholder）。 */
export const REPORT_JOB_TYPE_FINISHRATE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '1', label: '西醫UGY' },
  { value: '222', label: '中醫UGY' },
  { value: '223', label: '牙醫PGY' },
];

/** 課程下拉（classFormStatic.jsp classId :338-345；ajax 填充，雛形種子平列【雛形值待實機校準】）。 */
export const REPORT_CLASS_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'cls-1', label: '急救訓練 2026/01/12' },
  { value: 'cls-2', label: '感染管制 2026/02/09' },
  { value: 'cls-3', label: '醫學倫理 2026/03/16' },
];

// ---------------------------------------------------------------------------
// 批次 14 W2：查詢統計頁的結果表種子（掛各頁 resource 列上；body 只渲染帶統計鍵的列）。
// 【雛形值待實機校準】legacy 由 getLoginLogStaticAjax/getClassFormStaticAjax/
// getPlanFormStaticAjax/getCoreskillReportListAjax 等端點計算。
// ---------------------------------------------------------------------------

/** 登入使用狀況統計（LoginStatic.jsp doQuery；欄 yearMonth/loginTime/loginAccountNums）。
 *  種子帶查詢鍵值（matchesFilters 需命中；種子僅對應示範查詢值【雛形值待實機校準】）。 */
export const LOGIN_STATIC_RESULT_SEED: readonly Row[] = [
  { yearMonth: '2025/09', loginTime: 320, loginAccountNums: 96, logDateStart: '2025-09-01', logDateEnd: '2025-12-31' },
  { yearMonth: '2025/10', loginTime: 412, loginAccountNums: 103, logDateStart: '2025-09-01', logDateEnd: '2025-12-31' },
  { yearMonth: '2025/11', loginTime: 358, loginAccountNums: 99, logDateStart: '2025-09-01', logDateEnd: '2025-12-31' },
  { yearMonth: '2025/12', loginTime: 501, loginAccountNums: 112, logDateStart: '2025-09-01', logDateEnd: '2025-12-31' },
];

/** 課程滿意度統計（classFormStatic.jsp rowData :142-145；六統計值）；種子帶 classId 查詢鍵。 */
export const CLASS_FORM_STATIC_RESULT_SEED: readonly Row[] = [
  { fieldTitlem: '課程內容實用性', fieldSort: 1, averageValue: 4.52, minValue: 3, maxValue: 5, medianValue: 5, majorityValue: 5, classId: 'cls-1' },
  { fieldTitlem: '講師表達能力', fieldSort: 2, averageValue: 4.61, minValue: 4, maxValue: 5, medianValue: 5, majorityValue: 5, classId: 'cls-1' },
  { fieldTitlem: '時間安排適當性', fieldSort: 3, averageValue: 4.1, minValue: 2, maxValue: 5, medianValue: 4, majorityValue: 4, classId: 'cls-1' },
  { fieldTitlem: '場地與設備', fieldSort: 4, averageValue: 3.95, minValue: 2, maxValue: 5, medianValue: 4, majorityValue: 4, classId: 'cls-1' },
];

/** 主治醫師/住院醫師教學滿意度（planFormStatic.jsp rowData :129-132；同六統計值）；種子帶表單時間查詢鍵。 */
export const PLAN_FORM_STATIC_RESULT_SEED: readonly Row[] = [
  { fieldTitlem: '教學熱忱', fieldSort: 1, averageValue: 4.7, minValue: 4, maxValue: 5, medianValue: 5, majorityValue: 5, submitDateS: '2025-09-01', submitDateE: '2025-12-31' },
  { fieldTitlem: '回饋及指導', fieldSort: 2, averageValue: 4.55, minValue: 3, maxValue: 5, medianValue: 5, majorityValue: 5, submitDateS: '2025-09-01', submitDateE: '2025-12-31' },
  { fieldTitlem: '臨床教學技巧', fieldSort: 3, averageValue: 4.3, minValue: 3, maxValue: 5, medianValue: 4, majorityValue: 4, submitDateS: '2025-09-01', submitDateE: '2025-12-31' },
];

/** 教學門診稽核表統計（planFormStatic2.jsp rowData :147-148；passRate 0~1，渲染 ×100）；種子帶表單時間查詢鍵。 */
export const PLAN_FORM_STATIC2_RESULT_SEED: readonly Row[] = [
  { fieldTitlem: '病歷書記完整性', fieldSort: 1, scoreGroup: 'A', passRate: 0.923, phaseDepartmentId: 'dept-1', submitDateS: '2026-01-01', submitDateE: '2026-03-31' },
  { fieldTitlem: '理學檢查紀錄', fieldSort: 2, scoreGroup: 'A', passRate: 0.851, phaseDepartmentId: 'dept-1', submitDateS: '2026-01-01', submitDateE: '2026-03-31' },
  { fieldTitlem: '診斷與處置說明', fieldSort: 3, scoreGroup: 'A', passRate: 0.766, phaseDepartmentId: 'dept-2', submitDateS: '2026-01-01', submitDateE: '2026-03-31' },
  { fieldTitlem: '衛教溝通', fieldSort: 4, scoreGroup: 'A', passRate: 0.812, phaseDepartmentId: 'dept-2', submitDateS: '2026-01-01', submitDateE: '2026-03-31' },
  // 批次 33（RPT-001，issue #34）：scoreGroup B 群列（planFormStatic2.jsp :162-167——B 群不進表格，
  // optionNameList 計數進 pie；pieTitle＝fieldTitlem）。原種子無此列（pie 無資料來源），本批補。
  { fieldTitlem: '教學滿意度回饋', fieldSort: 5, scoreGroup: 'B', optionNameList: ['非常滿意', '滿意', '滿意', '普通', '滿意', '非常滿意'], phaseDepartmentId: 'dept-1', submitDateS: '2026-01-01', submitDateE: '2026-03-31' },
];

/** 核心技能統計(依技能)（coreSkillReportByForm.jsp columns :15-27；finishRate 由 need/real 計）；種子帶 queryType/annualId 查詢鍵。 */
export const CORE_SKILL_REPORT_BY_FORM_SEED: readonly Row[] = [
  { formName: '4-1基本及高階急救_(AHA)', needFillNum: 12, realFillNum: 10, queryType: '1', annualId: '114' },
  { formName: '4-2基本及高階呼吸道的處置', needFillNum: 12, realFillNum: 9, queryType: '1', annualId: '114' },
  { formName: '5-1教育計畫與學習護照', needFillNum: 8, realFillNum: 8, queryType: '1', annualId: '114' },
  { formName: '6-1醫療品質與病人安全', needFillNum: 10, realFillNum: 0, queryType: '1', annualId: '114' },
];

/** 核心技能統計(依學員)（coreSkillReportByStudent.jsp；formDesc 動態欄＋0.5 特例）；種子帶 queryType/annualId 查詢鍵。 */
export const CORE_SKILL_REPORT_BY_STUDENT_SEED: readonly Row[] = [
  {
    studentName: '學員甲', employeeId: 'EMP001', queryType: '0', annualId: '114',
    formDesc: [
      { formName: '4-1基本及高階急救_(AHA)', needFillNum: 12, realFillNum: 1 },
      { formName: '5-1教育計畫與學習護照', needFillNum: 8, realFillNum: 3 },
    ],
  },
  {
    studentName: '學員乙', employeeId: 'EMP002', queryType: '0', annualId: '114',
    formDesc: [
      { formName: '4-1基本及高階急救_(AHA)', needFillNum: 12, realFillNum: 2 },
      { formName: '5-1教育計畫與學習護照', needFillNum: 8, realFillNum: 0 },
    ],
  },
  {
    studentName: '學員丙', employeeId: 'EMP003', queryType: '0', annualId: '114',
    formDesc: [
      { formName: '4-1基本及高階急救_(AHA)', needFillNum: 12, realFillNum: 1 },
      { formName: '5-1教育計畫與學習護照', needFillNum: 8, realFillNum: 1 },
    ],
  },
];

// ---------------------------------------------------------------------------
// 批次 14 W3：academicMOM（學術會議紀錄）種子（academicMOM.jsp＋js-my/academicMOM.js）。
// 【雛形值待實機校準】legacy 由 getMOMData/getMOMBaseData/getMOMDetail 端點計算。
// ---------------------------------------------------------------------------

/** 九類課型（主表 thead :40-52 區塊；基準數表同 9 欄）。 */
export const MOM_COURSE_TYPES: readonly string[] = [
  '教學研究服務成效檢討會議',
  '晨報會',
  '雜誌研討會',
  '臨床研討會',
  '影像診療研討會',
  '臨床病理討論會',
  '死亡及併發症討論會',
  '各類醫事人員研討會',
  '實證醫學操作會議紀錄、簡報',
];

/** 學術會議紀錄主表（makeTable js:257-288：departmentName＋各課型 actual/limit＋完成度%＋備註）。 */
export const MOM_RESULT_SEED: readonly Row[] = [
  {
    departmentId: 'dept-1', departmentName: '內科部', totalLimit: 30, annual: '114',
    courseTypeData: [
      { courseTypeName: '教學研究服務成效檢討會議', actual: 4, limit: 4 },
      { courseTypeName: '晨報會', actual: 6, limit: 6 },
      { courseTypeName: '雜誌研討會', actual: 3, limit: 4 },
      { courseTypeName: '臨床研討會', actual: 5, limit: 5 },
      { courseTypeName: '影像診療研討會', actual: 2, limit: 3 },
      { courseTypeName: '臨床病理討論會', actual: 3, limit: 3 },
      { courseTypeName: '死亡及併發症討論會', actual: 2, limit: 2 },
      { courseTypeName: '各類醫事人員研討會', actual: 1, limit: 2 },
      { courseTypeName: '實證醫學操作會議紀錄、簡報', actual: 1, limit: 1 },
    ],
    StartDate: '2026-01', EndDate: '2026-01', jobTypeId: 'jt-1', organizationId: '7', status: '已完成',
  },
  {
    departmentId: 'dept-2', departmentName: '外科部', totalLimit: 26, annual: '114',
    StartDate: '2026-01', EndDate: '2026-01', jobTypeId: 'jt-1', organizationId: '7', status: '已完成',
    courseTypeData: [
      { courseTypeName: '教學研究服務成效檢討會議', actual: 4, limit: 4 },
      { courseTypeName: '晨報會', actual: 5, limit: 5 },
      { courseTypeName: '雜誌研討會', actual: 2, limit: 3 },
      { courseTypeName: '臨床研討會', actual: 4, limit: 4 },
      { courseTypeName: '影像診療研討會', actual: 1, limit: 2 },
      { courseTypeName: '臨床病理討論會', actual: 2, limit: 3 },
      { courseTypeName: '死亡及併發症討論會', actual: 2, limit: 2 },
      { courseTypeName: '各類醫事人員研討會', actual: 0, limit: 2 },
      { courseTypeName: '實證醫學操作會議紀錄、簡報', actual: 0, limit: 1 },
    ],
  },
  {
    departmentId: 'dept-3', departmentName: '小兒科部', totalLimit: 20, annual: '114',
    StartDate: '2026-01', EndDate: '2026-01', jobTypeId: 'jt-1', organizationId: '7', status: '已完成',
    courseTypeData: [
      { courseTypeName: '教學研究服務成效檢討會議', actual: 3, limit: 3 },
      { courseTypeName: '晨報會', actual: 4, limit: 4 },
      { courseTypeName: '雜誌研討會', actual: 2, limit: 2 },
      { courseTypeName: '臨床研討會', actual: 3, limit: 3 },
      { courseTypeName: '影像診療研討會', actual: 1, limit: 2 },
      { courseTypeName: '臨床病理討論會', actual: 1, limit: 2 },
      { courseTypeName: '死亡及併發症討論會', actual: 1, limit: 1 },
      { courseTypeName: '各類醫事人員研討會', actual: 1, limit: 2 },
      { courseTypeName: '實證醫學操作會議紀錄、簡報', actual: 0, limit: 1 },
    ],
  },
];

/** 基準數量表（makeBaseTable js:321-341：departmentName＋showBaseContent 9 欄）＋d_annual/createUser。 */
export const MOM_BASE_SEED: readonly Row[] = [
  {
    departmentName: '內科部', showBaseContent: [4, 6, 4, 5, 3, 3, 2, 2, 1], annualId: '114', jobTypeId: 'jt-1',
    annual: '114', createUserName: '教師甲', crateDate: '2026/01/05', organizationId: '7',
  },
  { departmentName: '外科部', showBaseContent: [4, 5, 3, 4, 2, 3, 2, 2, 1], annualId: '114', jobTypeId: 'jt-1', organizationId: '7' },
  { departmentName: '小兒科部', showBaseContent: [3, 4, 2, 3, 2, 2, 1, 2, 1], annualId: '114', jobTypeId: 'jt-1', organizationId: '7' },
];

/** 已完成表單明細（finishModal；openDetail js:152-195 動態表頭，雛形以固定三欄種子呈現）。 */
export const MOM_DETAIL_SEED: readonly Row[] = [
  { formName: '晨報會 2026/01/12', studentName: '學員甲', createDate: '2026/01/12' },
  { formName: '晨報會 2026/02/09', studentName: '學員乙', createDate: '2026/02/09' },
  { formName: '臨床研討會 2026/03/16', studentName: '學員丙', createDate: '2026/03/16' },
];

/** 核心技能認證情形（coreSkillStatisticReport.jsp：minObj/middleObj/maxObj 的 title/size/percent）。
 *  種子單列（legacy 資料由 statistic 頁查詢後存 session/window.opener 帶入）。 */
export const CORE_SKILL_STATISTIC_REPORT_SEED: readonly Row[] = [
  {
    planName: '一般醫學訓練計畫',
    minTitle: '低標（<20）', minSize: 6, minPercent: 15,
    middleTitle: '中標（20-30）', middleSize: 18, middlePercent: 45,
    maxTitle: '高標（>30）', maxSize: 16, maxPercent: 40,
  },
];

/**
 * 試卷派送清單種子（exam-dispatch-exam）：
 * - genCheckBox（dispatchExam.jsp:264-281）：testingListStatus==INIT(0)或 ANSWERED(2)→勾選欄隱藏。
 * - genScroe（:298-325）：testingList 空→未分派；0→未作答/2→待審核/3→已完成(帶分數)/4→逾期未作答。
 * 欄位鍵照 legacy 欄 data 名（examTargetIdName/studentName/createDateStr/finishDateStr/testingList）；
 * title/person/date/endDate 同步填（fixtures 產生鍵，通用視圖一致）。
 * 【雛形值待實機校準】
 */
const EXAM_DISPATCH_SEED: readonly Row[] = [
  {
    title: '一般醫學訓練計畫第一階段測驗', examTargetId: 'et-1', examTargetIdName: '一般醫學訓練計畫第一階段測驗',
    person: '學員甲', studentId: 'stu-1', studentName: '學員甲', date: '2026/01/05', createDateStr: '2026/01/05',
    endDate: '', finishDateStr: '', testingListStatus: '', dispatchStatus: '0', testingList: [],
  },
  {
    title: '一般醫學訓練計畫第一階段測驗', examTargetId: 'et-1', examTargetIdName: '一般醫學訓練計畫第一階段測驗',
    person: '學員乙', studentId: 'stu-2', studentName: '學員乙', date: '2026/01/05', createDateStr: '2026/01/05',
    endDate: '', finishDateStr: '', testingListStatus: '', dispatchStatus: '0', testingList: [],
  },
  {
    title: '一般醫學訓練計畫第二階段測驗', examTargetId: 'et-2', examTargetIdName: '一般醫學訓練計畫第二階段測驗',
    person: '學員丙', studentId: 'stu-3', studentName: '學員丙', date: '2026/01/05', createDateStr: '2026/01/05',
    endDate: '2026/02/10', finishDateStr: '2026/02/10', testingListStatus: '0', dispatchStatus: '1',
    testingList: [{ test_id: 't-1', test_status: '0', test_score: '' }],
  },
  {
    title: '住院醫師年度測驗', examTargetId: 'et-3', examTargetIdName: '住院醫師年度測驗',
    person: '學員丁', studentId: 'stu-4', studentName: '學員丁', date: '2026/01/05', createDateStr: '2026/01/05',
    endDate: '2026/02/10', finishDateStr: '2026/02/10', testingListStatus: '2', dispatchStatus: '1',
    testingList: [{ test_id: 't-2', test_status: '2', test_score: '' }],
  },
  {
    title: '住院醫師年度測驗', examTargetId: 'et-3', examTargetIdName: '住院醫師年度測驗',
    person: '學員戊', studentId: 'stu-5', studentName: '學員戊', date: '2026/01/05', createDateStr: '2026/01/05',
    endDate: '2026/02/10', finishDateStr: '2026/02/10', testingListStatus: '3', dispatchStatus: '1',
    testingList: [{ test_id: 't-3', test_status: '3', test_score: '85' }],
  },
  {
    title: '一般醫學訓練計畫第二階段測驗', examTargetId: 'et-2', examTargetIdName: '一般醫學訓練計畫第二階段測驗',
    person: '學員己', studentId: 'stu-6', studentName: '學員己', date: '2026/01/05', createDateStr: '2026/01/05',
    endDate: '2026/02/10', finishDateStr: '2026/02/10', testingListStatus: '4', dispatchStatus: '1',
    testingList: [{ test_id: 't-4', test_status: '4', test_score: '' }],
  },
];

/** 試卷維護種子（欄位鍵＝fixtures 產生鍵 title/score/title2＋modal 用 legacy 鍵＋examGroups）。 */
const EXAM_MANAGE_SEED: readonly Row[] = [
  {
    title: '急救訓練試卷', score: 3, title2: '急救訓練課後測驗', code: 'EXAM-001', name: '急救訓練試卷',
    memo: '急救訓練課後測驗', organizationId: 'org-1', jobTypeId: 'jt-1', departmentId: 'dept-1',
    level: '1', isLimitedTime: '0', testingTime: '30', remindTime: '5', effectiveDate: '2026/01/05',
    totalScore: 100, passingScore: 60, reviewTest: '1', sort: '1',
    examGroups: [
      {
        id: 'eg-1', name: '第一題組', sort: '1', examType: '1',
        details: [
          { id: 'egd-1', questionId: 'q-101', sort: '1', score: '40', bankName: '急救訓練題庫', questionType: '2', content: 'CPR 胸外按壓的深度應為多少？', answer: '5 至 6 公分', level: '1' },
          { id: 'egd-2', questionId: 'q-102', sort: '2', score: '30', bankName: '急救訓練題庫', questionType: '1', content: 'AED 使用前需確認患者無意識且無呼吸。', answer: '是', level: '1' },
          { id: 'egd-3', questionId: 'q-103', sort: '3', score: '30', bankName: '急救訓練題庫', questionType: '4', content: '簡述哈姆立克法的操作步驟。', answer: '環繞腹部快速向上向內擠壓', level: '5' },
        ],
      },
    ],
  },
  {
    title: '感染管制試卷', score: 0, title2: '感染管制課後測驗（尚未建立題組）', code: 'EXAM-002', name: '感染管制試卷',
    memo: '感染管制課後測驗（尚未建立題組）', organizationId: 'org-1', jobTypeId: 'jt-2', departmentId: 'dept-3',
    level: '5', isLimitedTime: '0', testingTime: '20', remindTime: '0', effectiveDate: '2026/01/05',
    totalScore: 0, passingScore: 0, reviewTest: '1', sort: '2', examGroups: [],
  },
  {
    title: '醫學倫理試卷', score: 2, title2: '醫學倫理年度評核', code: 'EXAM-003', name: '醫學倫理試卷',
    memo: '醫學倫理年度評核', organizationId: 'org-2', jobTypeId: 'jt-3', departmentId: 'dept-2',
    level: '10', isLimitedTime: '1', testingTime: '0', remindTime: '0', effectiveDate: '2026/01/05',
    totalScore: 60, passingScore: 60, reviewTest: '0', sort: '3',
    examGroups: [
      {
        id: 'eg-2', name: '倫理題組', sort: '1', examType: '2',
        details: [
          { id: 'egd-4', questionId: 'q-301', sort: '1', score: '30', bankName: '醫學倫理題庫', questionType: '2', content: '知情同意的四大要素為何？', answer: '揭露、理解、自願性、能力', level: '5' },
          { id: 'egd-5', questionId: 'q-302', sort: '2', score: '30', bankName: '醫學倫理題庫', questionType: '3', content: '下列哪些屬於病人自主權利法保障的範疇？', answer: '預立醫療決定、知情選擇、拒絕醫療', level: '10' },
        ],
      },
    ],
  },
  {
    title: '病歷寫作試卷', score: 0, title2: '病歷寫作入門測驗', code: 'EXAM-004', name: '病歷寫作試卷',
    memo: '病歷寫作入門測驗', organizationId: 'org-1', jobTypeId: 'jt-1', departmentId: 'dept-1',
    level: '5', isLimitedTime: '0', testingTime: '45', remindTime: '10', effectiveDate: '2026/01/05',
    totalScore: 0, passingScore: 0, reviewTest: '1', sort: '4', examGroups: [],
  },
];

// ---------------------------------------------------------------------------
// 批次 15（B7-1，Gitea issue #13）：訓練期程（training-plan-inst-plan-inst-list）
// 選項池＋主表種子＋大 modal 子資料（期程列/角色人員設定）＋明細報表＋表單明細。
// 行號基準＝ePortfolio_work/ePortfolio-tsgh/WebContent/WEB-INF/views/training/planInst/（親讀 2026-09-24）。
// legacy 由 getPhaseInstListAjax/getPlanInstReportAjax/getPlanInstFormReportAjax 伺服器組裝，
// 這裡全部為【雛形值待實機校準】。
// ---------------------------------------------------------------------------

/** 職類（planInst 查詢/modal 用；value 照 legacy 數字 id，3/225 才顯示每月期程匯入，planInstFormSearchPlanInst.jsp:31-37）。 */
export interface TrainingJobTypeOption {
  readonly value: string;
  readonly label: string;
  /** data-isgroup：是否分組（組別欄/範本下載驗證用，planInstList_js.jsp:191-202）。 */
  readonly isGroup: string;
}
export const TRAINING_JOB_TYPE_OPTIONS: readonly TrainingJobTypeOption[] = [
  { value: '3', label: '住院醫師（PGY）', isGroup: '1' },
  { value: '225', label: '西醫UGY見習醫師', isGroup: '0' },
  { value: '5', label: '牙醫住院醫師', isGroup: '0' },
  // 批次 49（b49-planinst）：查詢 form jobTypeId 的初始態選項（OPTION_SET_OVERRIDES
  // 'training-plan-inst-plan-inst-list¦jobTypeId' 引用同值）——legacy raw-dom:1383
  // option value=1「系統管理」data-isgroup=0（selected，planInstFormSearchPlanInst.jsp:125-129
  // setJobTypeElement 選項集的第一筆【雛形值待實機校準】）。查詢頁的 isGroup 判斷
  // （組別欄顯隱/範本下載驗證）由此池查表。
  { value: '1', label: '系統管理', isGroup: '0' },
];

/** 組別（phaseTargetId select；legacy setPhaseTargetGroupElement）。 */
export const TRAINING_PHASE_TARGET_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'pt-1', label: '第一組' },
  { value: 'pt-2', label: '第二組' },
];

/** 訓練地點（wardId select；legacy wardList，preOption `NA`，planInstModalPlanInst.jsp:276-282）。 */
export const TRAINING_WARD_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'w-1', label: '內科病房' },
  { value: 'w-2', label: '外科病房' },
  { value: 'w-3', label: '急診' },
];

/** 階段導師池（mentorList；legacy setAccountListByDepartmentIdNew 'Mentor'，planInstModalPlanInst.jsp:195-232）。 */
export const TRAINING_MENTOR_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 't-1', label: '教師甲' },
  { value: 't-2', label: '教師乙' },
  { value: 't-3', label: '教師丙' },
];

/** 臨床教師池（clinicalList；西醫UGY：第1順位住院醫師、第2順位主治醫生，planInstModalPlanInst.jsp:1010-1013）。 */
export const TRAINING_CLINICAL_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'c-1', label: '住院醫師丁' },
  { value: 'c-2', label: '主治醫師戊' },
  { value: 'c-3', label: '臨床教師己' },
];

/** 學員池（setStudentList；與 planInst 種子 studentId 對齊）。 */
export const TRAINING_STUDENT_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'stu-1', label: '學員甲' },
  { value: 'stu-2', label: '學員乙' },
  { value: 'stu-3', label: '學員丙' },
  { value: 'stu-4', label: '學員丁' },
  { value: 'stu-5', label: '學員戊' },
];

/** leaf 期程類型（constant.leafPhaseType 對照；這些類型的列才出現在大 modal 期程表）。 */
export const TRAINING_LEAF_PHASE_TYPES: readonly string[] = [
  'SubPhase',
  'ElectiveGroup',
  'RefElective1',
  'RefElective2',
  'RefElective3',
  'RefElective4',
  'RefElective5',
  'community',
  'communityIn',
];

/** leaf 階段池（subPhaseTargetList；已配置的不能改，planInstModalPlanInst.jsp:307-367）。 */
export interface TrainingSubPhaseTarget {
  readonly id: string;
  readonly name: string;
  readonly phaseType: string;
  readonly phaseDeptId: string;
}
export const TRAINING_SUB_PHASE_TARGETS: readonly TrainingSubPhaseTarget[] = [
  { id: 'sp-1', name: '門診學習（一）', phaseType: 'SubPhase', phaseDeptId: 'dept-1' },
  { id: 'sp-2', name: '病房學習（一）', phaseType: 'SubPhase', phaseDeptId: 'dept-1' },
  { id: 'sp-3', name: '急診學習', phaseType: 'SubPhase', phaseDeptId: 'dept-4' },
  { id: 'sp-4', name: '選修（一）', phaseType: 'RefElective1', phaseDeptId: 'dept-1' },
  { id: 'sp-5', name: '社區醫學', phaseType: 'community', phaseDeptId: 'dept-2' },
];

/** refId 對應清單（myCreateRefSelect：RefElective1-5/community/communityIn；preOption `請選擇`）。 */
export const TRAINING_REF_POOL: Readonly<
  Record<string, readonly { value: string; label: string }[]>
> = {
  RefElective1: [
    { value: 'ref-11', label: '心臟科選修' },
    { value: 'ref-12', label: '胸腔科選修' },
  ],
  RefElective2: [{ value: 'ref-13', label: '腎臟科選修' }],
  community: [
    { value: 'ref-21', label: '基層診所' },
    { value: 'ref-22', label: '衛生所' },
  ],
  communityIn: [{ value: 'ref-31', label: '院內社區' }],
};

/**
 * 訓練期程主表種子（鍵＝fixtures kind 鍵 date/title/person/category/person2＋modal/明細用 legacy 鍵）。
 * planInstList（主表列）/planInstRoles（角色人員設定，planInstModalPlanInst.jsp:699-757）/
 * planInstDetails（明細展開 getPlanInstReportAjax，planInstList_js.jsp:417-531）/
 * planInstForms（表單明細 getPlanInstFormReportAjax，planInstModalFormList.jsp:89-110）全部存列上。
 */
export const TRAINING_PLAN_INST_SEED: readonly Row[] = [
  {
    date: '115',
    title: '一般醫學訓練計畫',
    person: '學員甲',
    category: '第一組',
    person2: '教師甲',
    annualIdName: '115',
    planTargetId: 'plan-1',
    planTargetIdName: '一般醫學訓練計畫',
    isActive: '1',
    studentId: 'stu-1',
    studentIdName: '學員甲',
    phaseTargetId: 'pt-1',
    phaseTargetIdName: '第一組',
    mentorId: 't-1',
    mentorIdName: '教師甲',
    jobTypeId: '3',
    organizationId: 'org-1',
    planInstList: [
      { id: 'pi-1-1', phaseType: 'Plan', phaseTargetId: '-1', phaseTargetIdName: '一般醫學訓練計畫', startDateStr: '2026/01/01', endDateStr: '2026/12/31', mentorId1: '', mentorId2: '', clinicalId: '', clinicalId2: '', clinicalId3: '', wardId: '', isActive: '1', mentorAmount: '0', clinicalAmount: '0' },
      { id: 'pi-1-2', phaseType: 'Group', phaseTargetId: '-1', phaseTargetIdName: '第一組', startDateStr: '2026/01/01', endDateStr: '2026/06/30', mentorId1: '', mentorId2: '', clinicalId: '', clinicalId2: '', clinicalId3: '', wardId: '', isActive: '1', mentorAmount: '0', clinicalAmount: '0' },
      { id: 'pi-1-3', phaseType: 'Phase', phaseTargetId: '-1', phaseTargetIdName: '內科部', startDateStr: '2026/01/01', endDateStr: '2026/03/31', mentorId1: 't-1', mentorId2: 't-2', clinicalId: 'c-1', clinicalId2: 'c-2', clinicalId3: '', wardId: '', isActive: '1', mentorAmount: '2', clinicalAmount: '2' },
      { id: 'pi-1-4', phaseType: 'SubPhase', phaseTargetId: 'sp-1', phaseTargetIdName: '門診學習（一）', startDateStr: '2026/01/01', endDateStr: '2026/02/28', mentorId1: 't-1', mentorId2: '', clinicalId: 'c-1', clinicalId2: '', clinicalId3: '', wardId: 'w-1', isActive: '1', mentorAmount: '1', clinicalAmount: '1' },
      { id: 'pi-1-5', phaseType: 'RefElective1', phaseTargetId: 'sp-4', phaseTargetIdName: '選修（一）', refId: 'ref-11', startDateStr: '2026/03/01', endDateStr: '', mentorId1: '', mentorId2: '', clinicalId: 'c-3', clinicalId2: '', clinicalId3: '', wardId: '', isActive: '0', mentorAmount: '0', clinicalAmount: '1' },
    ],
    planInstRoles: [
      { phaseTargetRoleId: 'pr-11', phaseTargetId: 'plan-1', phaseInstId: 'pi-1-4', roleName: '案例討論會主持人', flowRole: 'Any', accountId: 't-1', accountName: '教師甲' },
      { phaseTargetRoleId: 'pr-12', phaseTargetId: 'plan-1', phaseInstId: 'pi-1-5', roleName: '社區指導員', flowRole: 'Clinical', accountId: 'c-3', accountName: '臨床教師己' },
    ],
    planInstDetails: [
      { id: 'pi-1-1', phaseType: 'Plan', period: '2026/01/01~2026/12/31', phaseIdName: '一般醫學訓練計畫', subPhaseIdIdName: '', refIdName: '', mentorId1Name: '', mentorId2Name: '', clinicalIdName: '', clinicalId2Name: '', clinicalId3Name: '', wardIdName: '', formDone: '2', formTodo: '6', formDonePercentage: '33', examDone: '1', examTodo: '3', examDonePercentage: '33', isActive: '1' },
      { id: 'pi-1-2', phaseType: 'Group', period: '2026/01/01~2026/06/30', phaseIdName: '第一組', subPhaseIdIdName: '', refIdName: '', mentorId1Name: '', mentorId2Name: '', clinicalIdName: '', clinicalId2Name: '', clinicalId3Name: '', wardIdName: '', formDone: '2', formTodo: '4', formDonePercentage: '50', examDone: '1', examTodo: '2', examDonePercentage: '50', isActive: '1' },
      { id: 'pi-1-3', phaseType: 'Phase', period: '2026/01/01~2026/03/31', phaseIdName: '內科部', subPhaseIdIdName: '', refIdName: '', mentorId1Name: '教師甲', mentorId2Name: '教師乙', clinicalIdName: '住院醫師丁', clinicalId2Name: '主治醫師戊', clinicalId3Name: '', wardIdName: '', formDone: '1', formTodo: '2', formDonePercentage: '50', examDone: '1', examTodo: '1', examDonePercentage: '100', isActive: '1' },
      { id: 'pi-1-4', phaseType: 'SubPhase', period: '2026/01/01~2026/02/28', phaseIdName: '', subPhaseIdIdName: '門診學習（一）', refIdName: '', mentorId1Name: '教師甲', mentorId2Name: '', clinicalIdName: '住院醫師丁', clinicalId2Name: '', clinicalId3Name: '', wardIdName: '內科病房', formDone: '1', formTodo: '1', formDonePercentage: '100', examDone: '1', examTodo: '1', examDonePercentage: '100', isActive: '1' },
      { id: 'pi-1-5', phaseType: 'community', period: '2026/03/01~', phaseIdName: '選修（一）', subPhaseIdIdName: '選修（一）', refIdName: '心臟科選修', mentorId1Name: '', mentorId2Name: '', clinicalIdName: '臨床教師己', clinicalId2Name: '', clinicalId3Name: '', wardIdName: '', formDone: '0', formTodo: '1', formDonePercentage: '0', examDone: '0', examTodo: '1', examDonePercentage: '0', isActive: '0' },
    ],
    planInstForms: [
      { period: '2026/01/01~2026/02/28', phaseIdName: '內科部', subPhaseIdIdName: '門診學習（一）', formList: [
        { formKey: 'fk-101', formTemplateId: 'ft-1', formTemplateName: '學習紀錄表單', formTargetTypeIdName: '病例寫作', formTargetTypeDetailIdName: '門診', formTargetId: 'target-1', phaseInstId: 'pi-1-4', refTable: 'StudyRecordForm', refId: '9', formId: 'f-101', isDone: '1', dispatchType: '0' },
        { formKey: 'fk-102', formTemplateId: 'ft-2', formTemplateName: '訓練回饋表', formTargetTypeIdName: '病例寫作', formTargetTypeDetailIdName: '病房', formTargetId: 'target-2', phaseInstId: 'pi-1-4', refTable: '', refId: '', formId: '', isDone: '0', dispatchType: '1' },
      ] },
      { period: '2026/03/01~', phaseIdName: '選修（一）', subPhaseIdIdName: '選修（一）', formList: [
        { formKey: 'fk-103', formTemplateId: 'ft-3', formTemplateName: '實地操作評值表', formTargetTypeIdName: '操作評值', formTargetTypeDetailIdName: '', formTargetId: 'target-3', phaseInstId: 'pi-1-5', refTable: 'StudyRecordForm', refId: '12', formId: 'f-103', isDone: '0', dispatchType: '2' },
      ] },
    ],
  },
  {
    date: '115',
    title: '一般醫學訓練計畫',
    person: '學員乙',
    category: '第一組',
    person2: '教師乙',
    annualIdName: '115',
    planTargetId: 'plan-1',
    planTargetIdName: '一般醫學訓練計畫',
    isActive: '1',
    studentId: 'stu-2',
    studentIdName: '學員乙',
    phaseTargetId: 'pt-1',
    phaseTargetIdName: '第一組',
    mentorId: 't-2',
    mentorIdName: '教師乙',
    jobTypeId: '3',
    organizationId: 'org-1',
    planInstList: [
      { id: 'pi-2-1', phaseType: 'Plan', phaseTargetId: '-1', phaseTargetIdName: '一般醫學訓練計畫', startDateStr: '2026/01/01', endDateStr: '2026/12/31', mentorId1: '', mentorId2: '', clinicalId: '', clinicalId2: '', clinicalId3: '', wardId: '', isActive: '1', mentorAmount: '0', clinicalAmount: '0' },
      { id: 'pi-2-2', phaseType: 'Group', phaseTargetId: '-1', phaseTargetIdName: '第一組', startDateStr: '2026/01/01', endDateStr: '2026/06/30', mentorId1: '', mentorId2: '', clinicalId: '', clinicalId2: '', clinicalId3: '', wardId: '', isActive: '1', mentorAmount: '0', clinicalAmount: '0' },
      { id: 'pi-2-3', phaseType: 'Phase', phaseTargetId: '-1', phaseTargetIdName: '外科部', startDateStr: '2026/01/01', endDateStr: '2026/04/30', mentorId1: 't-2', mentorId2: '', clinicalId: 'c-2', clinicalId2: '', clinicalId3: '', wardId: '', isActive: '1', mentorAmount: '1', clinicalAmount: '1' },
      { id: 'pi-2-4', phaseType: 'SubPhase', phaseTargetId: 'sp-2', phaseTargetIdName: '病房學習（一）', startDateStr: '2026/01/01', endDateStr: '2026/03/31', mentorId1: 't-2', mentorId2: '', clinicalId: 'c-2', clinicalId2: '', clinicalId3: '', wardId: 'w-2', isActive: '1', mentorAmount: '1', clinicalAmount: '1' },
    ],
    planInstRoles: [],
    planInstDetails: [
      { id: 'pi-2-1', phaseType: 'Plan', period: '2026/01/01~2026/12/31', phaseIdName: '一般醫學訓練計畫', subPhaseIdIdName: '', refIdName: '', mentorId1Name: '', mentorId2Name: '', clinicalIdName: '', clinicalId2Name: '', clinicalId3Name: '', wardIdName: '', formDone: '1', formTodo: '5', formDonePercentage: '20', examDone: '0', examTodo: '2', examDonePercentage: '0', isActive: '1' },
      { id: 'pi-2-3', phaseType: 'Phase', period: '2026/01/01~2026/04/30', phaseIdName: '外科部', subPhaseIdIdName: '', refIdName: '', mentorId1Name: '教師乙', mentorId2Name: '', clinicalIdName: '主治醫師戊', clinicalId2Name: '', clinicalId3Name: '', wardIdName: '', formDone: '1', formTodo: '3', formDonePercentage: '33', examDone: '0', examTodo: '1', examDonePercentage: '0', isActive: '1' },
      { id: 'pi-2-4', phaseType: 'SubPhase', period: '2026/01/01~2026/03/31', phaseIdName: '', subPhaseIdIdName: '病房學習（一）', refIdName: '', mentorId1Name: '教師乙', mentorId2Name: '', clinicalIdName: '主治醫師戊', clinicalId2Name: '', clinicalId3Name: '', wardIdName: '外科病房', formDone: '1', formTodo: '1', formDonePercentage: '100', examDone: '0', examTodo: '1', examDonePercentage: '0', isActive: '1' },
    ],
    planInstForms: [
      { period: '2026/01/01~2026/03/31', phaseIdName: '外科部', subPhaseIdIdName: '病房學習（一）', formList: [
        { formKey: 'fk-201', formTemplateId: 'ft-1', formTemplateName: '學習紀錄表單', formTargetTypeIdName: '病例寫作', formTargetTypeDetailIdName: '病房', formTargetId: 'target-4', phaseInstId: 'pi-2-4', refTable: 'StudyRecordForm', refId: '21', formId: 'f-201', isDone: '0', dispatchType: '1' },
      ] },
    ],
  },
  {
    date: '114',
    title: '住院醫師訓練計畫',
    person: '學員丙',
    category: '',
    person2: '教師丙',
    annualIdName: '114',
    planTargetId: 'plan-2',
    planTargetIdName: '住院醫師訓練計畫',
    isActive: '0',
    studentId: 'stu-3',
    studentIdName: '學員丙',
    phaseTargetId: '',
    phaseTargetIdName: '',
    mentorId: 't-3',
    mentorIdName: '教師丙',
    jobTypeId: '225',
    organizationId: 'org-1',
    planInstList: [
      { id: 'pi-3-1', phaseType: 'Plan', phaseTargetId: '-1', phaseTargetIdName: '住院醫師訓練計畫', startDateStr: '2025/01/01', endDateStr: '2025/12/31', mentorId1: '', mentorId2: '', clinicalId: '', clinicalId2: '', clinicalId3: '', wardId: '', isActive: '0', mentorAmount: '0', clinicalAmount: '0' },
      { id: 'pi-3-2', phaseType: 'SubPhase', phaseTargetId: 'sp-3', phaseTargetIdName: '急診學習', startDateStr: '2025/07/01', endDateStr: '2025/09/30', mentorId1: 't-3', mentorId2: '', clinicalId: 'c-1', clinicalId2: '', clinicalId3: '', wardId: 'w-3', isActive: '0', mentorAmount: '1', clinicalAmount: '1' },
    ],
    planInstRoles: [],
    planInstDetails: [
      { id: 'pi-3-1', phaseType: 'Plan', period: '2025/01/01~2025/12/31', phaseIdName: '住院醫師訓練計畫', subPhaseIdIdName: '', refIdName: '', mentorId1Name: '', mentorId2Name: '', clinicalIdName: '', clinicalId2Name: '', clinicalId3Name: '', wardIdName: '', formDone: '3', formTodo: '4', formDonePercentage: '75', examDone: '2', examTodo: '2', examDonePercentage: '100', isActive: '0' },
      { id: 'pi-3-2', phaseType: 'SubPhase', period: '2025/07/01~2025/09/30', phaseIdName: '', subPhaseIdIdName: '急診學習', refIdName: '', mentorId1Name: '教師丙', mentorId2Name: '', clinicalIdName: '住院醫師丁', clinicalId2Name: '', clinicalId3Name: '', wardIdName: '急診', formDone: '1', formTodo: '1', formDonePercentage: '100', examDone: '1', examTodo: '1', examDonePercentage: '100', isActive: '0' },
    ],
    planInstForms: [],
  },
  {
    date: '115',
    title: '住院醫師訓練計畫',
    person: '學員丁',
    category: '',
    person2: '教師甲',
    annualIdName: '115',
    planTargetId: 'plan-2',
    planTargetIdName: '住院醫師訓練計畫',
    isActive: '1',
    studentId: 'stu-4',
    studentIdName: '學員丁',
    phaseTargetId: '',
    phaseTargetIdName: '',
    mentorId: 't-1',
    mentorIdName: '教師甲',
    jobTypeId: '225',
    organizationId: 'org-2',
    planInstList: [
      { id: 'pi-4-1', phaseType: 'Plan', phaseTargetId: '-1', phaseTargetIdName: '住院醫師訓練計畫', startDateStr: '2026/01/01', endDateStr: '2026/12/31', mentorId1: '', mentorId2: '', clinicalId: '', clinicalId2: '', clinicalId3: '', wardId: '', isActive: '1', mentorAmount: '0', clinicalAmount: '0' },
    ],
    planInstRoles: [],
    planInstDetails: [
      { id: 'pi-4-1', phaseType: 'Plan', period: '2026/01/01~2026/12/31', phaseIdName: '住院醫師訓練計畫', subPhaseIdIdName: '', refIdName: '', mentorId1Name: '', mentorId2Name: '', clinicalIdName: '', clinicalId2Name: '', clinicalId3Name: '', wardIdName: '', formDone: '0', formTodo: '4', formDonePercentage: '0', examDone: '0', examTodo: '2', examDonePercentage: '0', isActive: '1' },
    ],
    planInstForms: [],
  },
  {
    date: '115',
    title: '一般醫學訓練計畫',
    person: '學員戊',
    category: '第二組',
    person2: '教師丙',
    annualIdName: '115',
    planTargetId: 'plan-1',
    planTargetIdName: '一般醫學訓練計畫',
    isActive: '1',
    studentId: 'stu-5',
    studentIdName: '學員戊',
    phaseTargetId: 'pt-2',
    phaseTargetIdName: '第二組',
    mentorId: 't-3',
    mentorIdName: '教師丙',
    jobTypeId: '3',
    organizationId: 'org-1',
    planInstList: [
      { id: 'pi-5-1', phaseType: 'Plan', phaseTargetId: '-1', phaseTargetIdName: '一般醫學訓練計畫', startDateStr: '2026/01/01', endDateStr: '2026/12/31', mentorId1: '', mentorId2: '', clinicalId: '', clinicalId2: '', clinicalId3: '', wardId: '', isActive: '1', mentorAmount: '0', clinicalAmount: '0' },
      { id: 'pi-5-2', phaseType: 'Group', phaseTargetId: '-1', phaseTargetIdName: '第二組', startDateStr: '2026/01/01', endDateStr: '2026/06/30', mentorId1: '', mentorId2: '', clinicalId: '', clinicalId2: '', clinicalId3: '', wardId: '', isActive: '1', mentorAmount: '0', clinicalAmount: '0' },
      { id: 'pi-5-3', phaseType: 'community', phaseTargetId: 'sp-5', phaseTargetIdName: '社區醫學', refId: 'ref-21', startDateStr: '2026/02/01', endDateStr: '', mentorId1: '', mentorId2: '', clinicalId: 'c-3', clinicalId2: '', clinicalId3: '', wardId: '', isActive: '1', mentorAmount: '0', clinicalAmount: '1' },
    ],
    planInstRoles: [],
    planInstDetails: [
      { id: 'pi-5-1', phaseType: 'Plan', period: '2026/01/01~2026/12/31', phaseIdName: '一般醫學訓練計畫', subPhaseIdIdName: '', refIdName: '', mentorId1Name: '', mentorId2Name: '', clinicalIdName: '', clinicalId2Name: '', clinicalId3Name: '', wardIdName: '', formDone: '2', formTodo: '6', formDonePercentage: '33', examDone: '1', examTodo: '3', examDonePercentage: '33', isActive: '1' },
      { id: 'pi-5-3', phaseType: 'community', period: '2026/02/01~', phaseIdName: '社區醫學', subPhaseIdIdName: '社區醫學', refIdName: '基層診所', mentorId1Name: '', mentorId2Name: '', clinicalIdName: '臨床教師己', clinicalId2Name: '', clinicalId3Name: '', wardIdName: '', formDone: '2', formTodo: '2', formDonePercentage: '100', examDone: '1', examTodo: '1', examDonePercentage: '100', isActive: '1' },
    ],
    planInstForms: [
      { period: '2026/02/01~', phaseIdName: '社區醫學', subPhaseIdIdName: '社區醫學', formList: [
        { formKey: 'fk-501', formTemplateId: 'ft-2', formTemplateName: '訓練回饋表', formTargetTypeIdName: '社區', formTargetTypeDetailIdName: '', formTargetId: 'target-5', phaseInstId: 'pi-5-3', refTable: 'StudyRecordForm', refId: '51', formId: 'f-501', isDone: '1', dispatchType: '0' },
      ] },
    ],
  },
];

/** 新增時的預設值（legacy showModalPlanInst add＝帶查詢表單現值；planInstModalPlanInst.jsp:791-799）。 */
export const TRAINING_PLAN_INST_DEFAULTS: Readonly<Record<string, string>> = {
  annualId: '115',
  organizationId: 'org-1',
  jobTypeId: '3',
  planTargetId: 'plan-1',
  phaseTargetId: 'pt-1',
  studentId: '-1',
  studentIdName: '',
  mentorId: '-1',
  mentorIdName: '',
};

// ---------------------------------------------------------------------------
// 批次 15 Wave 2（B7-2，issue #13）：學習紀錄流程群。
// 基準＝ePortfolio_work/.../views/training/studyRecord/（studyRecordFill.jsp 265 行、
// studyRecordStatus.jsp 388 行、fillChoiceModal.jsp 245 行、studyRecordFormModal.jsp 341 行、
// recordFormTemplate.jsp 110 行、targetDetailStatus.jsp 105 行、changeFillerModal.jsp 53 行）。
// 暫存清單/已填明細/學習項目/完成狀態皆為 ajax 組裝 → 種子【雛形值待實機校準】。
// ---------------------------------------------------------------------------

/**
 * 學習紀錄填寫（fill）主表種子：鍵＝fixtures kind 鍵（text2=已填明細留空、category=訓練階段、
 * title=學習目標、score=目標數量、score2=完成數、text3=填寫中簽核中、date=開始日、endDate=應完成日）
 * ＋行為用 legacy 鍵。isCrossTargetFill==1＝跨填（填寫選擇欄變 checkbox）。
 */
export const TRAINING_STUDY_RECORD_FILL_SEED: readonly Row[] = [
  {
    category: '內科部 門診學習（一）',
    title: '病例寫作紀錄',
    score: '必修 2',
    score2: '1/2',
    text3: '1',
    date: '2026/01/01',
    endDate: '2026/02/28',
    phaseName: '內科部 門診學習（一）',
    studyTargetName: '病例寫作紀錄',
    formTemplateId: 'ft-101',
    caseType: '1',
    caseQty: '2',
    sumCaseQty: '0',
    doneQty: '1',
    ingQty: '1',
    phaseStartDateString: '2026/01/01',
    phaseEndDateString: '2026/02/28',
    isCrossTargetFill: '0',
    fillType: 'single',
    studyTargetId: 'st-1',
    studentId: 'stu-1',
    annualId: '115',
    phaseTargetId: 'sp-1',
  },
  {
    category: '外科部 病房學習（一）',
    title: '手術紀錄表單',
    score: '3 選 2',
    score2: '0/2',
    text3: '0',
    date: '2026/01/01',
    endDate: '2026/03/31',
    phaseName: '外科部 病房學習（一）',
    studyTargetName: '手術紀錄表單',
    formTemplateId: 'ft-102',
    caseType: '2',
    caseQty: '2',
    sumCaseQty: '3',
    doneQty: '0',
    ingQty: '0',
    phaseStartDateString: '2026/01/01',
    phaseEndDateString: '2026/03/31',
    isCrossTargetFill: '1',
    fillType: 'share',
    studyTargetId: 'st-2',
    studentId: 'stu-1',
    annualId: '115',
    phaseTargetId: 'sp-2',
  },
  {
    category: '急診 急診學習',
    title: '急診處置紀錄',
    score: '3 選 2',
    score2: '0/2',
    text3: '0',
    date: '2026/01/01',
    endDate: '2026/03/31',
    phaseName: '急診 急診學習',
    studyTargetName: '急診處置紀錄',
    formTemplateId: 'ft-102',
    caseType: '2',
    caseQty: '2',
    sumCaseQty: '3',
    doneQty: '0',
    ingQty: '0',
    phaseStartDateString: '2026/01/01',
    phaseEndDateString: '2026/03/31',
    isCrossTargetFill: '1',
    fillType: 'share',
    studyTargetId: 'st-3',
    studentId: 'stu-1',
    annualId: '115',
    phaseTargetId: 'sp-3',
  },
  {
    category: '內科部 門診學習（一）',
    title: '衛教紀錄',
    score: '必修 1',
    score2: '1/1',
    text3: '0',
    date: '2026/01/01',
    endDate: '2026/02/28',
    phaseName: '內科部 門診學習（一）',
    studyTargetName: '衛教紀錄',
    formTemplateId: 'ft-103',
    caseType: '1',
    caseQty: '1',
    sumCaseQty: '0',
    doneQty: '1',
    ingQty: '0',
    phaseStartDateString: '2026/01/01',
    phaseEndDateString: '2026/02/28',
    isCrossTargetFill: '0',
    fillType: 'single',
    studyTargetId: 'st-4',
    studentId: 'stu-1',
    annualId: '115',
    phaseTargetId: 'sp-1',
  },
];

/**
 * 學習紀錄查詢（status）主表種子：比 fill 多 訓練計畫/學員 兩欄；其餘同構。
 * （雛形 admin 視角＝isPermitUpdate true，D9；isStudent=false。）
 */
export const TRAINING_STUDY_RECORD_STATUS_SEED: readonly Row[] = [
  {
    title: '一般醫學訓練計畫',
    person: '學員甲',
    category: '內科部 門診學習（一）',
    title2: '病例寫作紀錄',
    score: '必修 2',
    score2: '1/2',
    text2: '1',
    date: '2026/01/01',
    endDate: '2026/02/28',
    planTargetName: '一般醫學訓練計畫',
    studentName: '學員甲',
    phaseName: '內科部 門診學習（一）',
    studyTargetName: '病例寫作紀錄',
    formTemplateId: 'ft-101',
    caseType: '1',
    caseQty: '2',
    sumCaseQty: '0',
    doneQty: '1',
    ingQty: '1',
    phaseStartDateString: '2026/01/01',
    phaseEndDateString: '2026/02/28',
    studyTargetId: 'st-1',
    studentId: 'stu-1',
    finishStatus: 'ALL',
  },
  {
    title: '一般醫學訓練計畫',
    person: '學員乙',
    category: '外科部 病房學習（一）',
    title2: '手術紀錄表單',
    score: '3 選 2',
    score2: '2/2',
    text2: '0',
    date: '2026/01/01',
    endDate: '2026/03/31',
    planTargetName: '一般醫學訓練計畫',
    studentName: '學員乙',
    phaseName: '外科部 病房學習（一）',
    studyTargetName: '手術紀錄表單',
    formTemplateId: 'ft-102',
    caseType: '2',
    caseQty: '2',
    sumCaseQty: '3',
    doneQty: '2',
    ingQty: '0',
    phaseStartDateString: '2026/01/01',
    phaseEndDateString: '2026/03/31',
    studyTargetId: 'st-2',
    studentId: 'stu-2',
    finishStatus: 'ALL',
  },
  {
    title: '住院醫師訓練計畫',
    person: '學員丙',
    category: '急診 急診學習',
    title2: '急診處置紀錄',
    score: '必修 2',
    score2: '0/2',
    text2: '2',
    date: '2025/07/01',
    endDate: '2025/09/30',
    planTargetName: '住院醫師訓練計畫',
    studentName: '學員丙',
    phaseName: '急診 急診學習',
    studyTargetName: '急診處置紀錄',
    formTemplateId: 'ft-101',
    caseType: '1',
    caseQty: '2',
    sumCaseQty: '0',
    doneQty: '0',
    ingQty: '2',
    phaseStartDateString: '2025/07/01',
    phaseEndDateString: '2025/09/30',
    studyTargetId: 'st-3',
    studentId: 'stu-3',
    finishStatus: 'ALL',
  },
];

/** 暫存或待填清單（getTodoStudyRecordListAjax；showFill 的 phaseTargetId/formKey；拒絕理由）。 */
export const TRAINING_STUDY_RECORD_DRAFT_SEED: readonly Row[] = [
  {
    studyRecordFormId: '9',
    createDate: '2026/01/05',
    formKey: 'fk-101',
    phaseTargetId: 'sp-1',
    rejectReason: '',
  },
  {
    studyRecordFormId: '12',
    createDate: '2026/01/04',
    formKey: 'fk-103',
    phaseTargetId: 'sp-4',
    rejectReason: '病例主訴段落未填寫，請補正後重新提交',
  },
];

/**
 * 已填明細（getStudyTargetFormListAjax；recordFormTemplate.jsp 表：全選/紀錄建立時間/
 * 學習項目/簽核流程；genFlowList 的 flowList 結構含 done/added/filler1）。存主表列上。
 */
export const TRAINING_STUDY_RECORD_FORMS_SEED: Row = {
  'st-1': [
    {
      studyRecordFormId: '9',
      formKey: 'fk-101',
      phaseTargetId: 'sp-1',
      createDateString: '2026/01/05 09:12',
      formId: 'f-101',
      done: false,
      currentFlowRole: 'Mentor',
      studyItems: ['門診病歷書寫（一）', '器械準備確認'],
      flowList: [
        { id: 'fl-1', formId: 'f-101', flowRole: 'Student', flowRoleName: '學員', accountName: '學員甲', done: true, added: false, filler1Id: 0, filler1Name: '' },
        { id: 'fl-2', formId: 'f-101', flowRole: 'Mentor', flowRoleName: '導師', accountName: '教師甲', done: false, added: false, filler1Id: 0, filler1Name: '' },
      ],
    },
    {
      studyRecordFormId: '11',
      formKey: 'fk-104',
      phaseTargetId: 'sp-1',
      createDateString: '2026/01/03 14:30',
      formId: 'f-104',
      done: true,
      currentFlowRole: '',
      studyItems: ['門診病歷書寫（二）'],
      flowList: [
        { id: 'fl-3', formId: 'f-104', flowRole: 'Student', flowRoleName: '學員', accountName: '學員甲', done: true, added: false, filler1Id: 0, filler1Name: '' },
        { id: 'fl-4', formId: 'f-104', flowRole: 'Mentor', flowRoleName: '導師', accountName: '教師乙', done: true, added: false, filler1Id: 0, filler1Name: '' },
      ],
    },
  ],
  'st-2': [
    {
      studyRecordFormId: '15',
      formKey: 'fk-102',
      phaseTargetId: 'sp-2',
      createDateString: '2026/01/04 10:05',
      formId: 'f-102',
      done: true,
      currentFlowRole: '',
      studyItems: ['手術紀錄（一）', '術前評估'],
      flowList: [
        { id: 'fl-5', formId: 'f-102', flowRole: 'Student', flowRoleName: '學員', accountName: '學員乙', done: true, added: false, filler1Id: 0, filler1Name: '' },
      ],
    },
  ],
  'st-3': [],
};

/**
 * 填寫選擇（fillChoiceModal getStudyTargetListForFillAjax）：學習項目分組（target.name bg-success）
 * ＋項目列（ccCheckBox＋應完成數 caseQty/已填寫數 doneQty/未填寫數 xxQty）；formTemplateVersionId
 * ＋簽核流程（TRAINING_FORM_FLOW_SEED 同構）。存主表列上（studyItems）。
 */
export const TRAINING_STUDY_RECORD_ITEMS_SEED: Row = {
  'st-1': [
    { studyItemId: 'si-11', studyItemName: '門診病歷書寫（一）', caseQty: '1', doneQty: '0', xxQty: '1' },
    { studyItemId: 'si-12', studyItemName: '門診病歷書寫（二）', caseQty: '1', doneQty: '1', xxQty: '0' },
  ],
  'st-2': [
    { studyItemId: 'si-21', studyItemName: '手術紀錄（一）', caseQty: '1', doneQty: '0', xxQty: '1' },
    { studyItemId: 'si-22', studyItemName: '手術紀錄（二）', caseQty: '1', doneQty: '0', xxQty: '1' },
    { studyItemId: 'si-23', studyItemName: '術前評估', caseQty: '1', doneQty: '2', xxQty: '0' },
  ],
  'st-3': [
    { studyItemId: 'si-31', studyItemName: '急診初始評估', caseQty: '1', doneQty: '0', xxQty: '1' },
    { studyItemId: 'si-32', studyItemName: '急診處置記錄', caseQty: '1', doneQty: '0', xxQty: '1' },
  ],
  'st-4': [
    { studyItemId: 'si-41', studyItemName: '衛教執行紀錄', caseQty: '1', doneQty: '0', xxQty: '1' },
  ],
};

/** 學習項目完成狀態（getStudyTargetDetailStatusListAjax；targetDetailStatus.jsp 表 4 欄）。 */
export const TRAINING_TARGET_DETAIL_SEED: Row = {
  'st-1': [
    { studyItemName: '門診病歷書寫（一）', caseQty: '1', caseType: '1', doneQty: '1', ingQty: '1' },
    { studyItemName: '門診病歷書寫（二）', caseQty: '1', caseType: '1', doneQty: '1', ingQty: '0' },
  ],
  'st-2': [
    { studyItemName: '手術紀錄（一）', caseQty: '1', caseType: '2', doneQty: '2', ingQty: '0' },
    { studyItemName: '手術紀錄（二）', caseQty: '1', caseType: '2', doneQty: '0', ingQty: '0' },
  ],
  'st-3': [
    { studyItemName: '急診初始評估', caseQty: '2', caseType: '1', doneQty: '0', ingQty: '2' },
    { studyItemName: '急診處置記錄', caseQty: '2', caseType: '1', doneQty: '0', ingQty: '0' },
  ],
  'st-4': [
    { studyItemName: '衛教執行紀錄', caseQty: '1', caseType: '1', doneQty: '1', ingQty: '0' },
  ],
};

// ---------------------------------------------------------------------------
// 批次 15 Wave 3（B7-3，issue #13）：成績單（training-transcript-list）種子。
// transcriptTargetList（genFormTableHead :207-259：name(weight%) 欄，同名同權重合併）＋
// getTranscriptListAjax 成績列（:339-421）。【雛形值待實機校準】
// ---------------------------------------------------------------------------

/** 成績目標欄（genFormTableHead；weight 為綜合表現給分機制權重）。 */
export interface TranscriptTarget {
  readonly id: string;
  readonly name: string;
  readonly weight: string;
}
export const TRANSCRIPT_TARGETS: readonly TranscriptTarget[] = [
  { id: 'tt-1', name: '臨床案例討論', weight: '30' },
  { id: 'tt-2', name: '病例寫作', weight: '40' },
  { id: 'tt-3', name: '核心課程', weight: '30' },
];

/** 成績列種子（transcriptDetailList：score＋transcriptTargetId；changeable==1→未勾+enabled）。 */
export const TRANSCRIPT_RESULT_SEED: readonly Row[] = [
  {
    phaseInstId: 'pi-1-3',
    changeable: '1',
    queryDeptType: '0',
    phaseStartDateStr: '2026/01/01',
    phaseEndDateStr: '2026/03/31',
    phaseName: '內科部',
    studentEmployeeId: 'EMP001',
    schoolId: 'S001',
    studentName: '學員甲',
    clinicalName: '住院醫師丁',
    transcriptDetailList: [
      { transcriptTargetId: 'tt-1', score: '85' },
      { transcriptTargetId: 'tt-2', score: '78' },
      { transcriptTargetId: 'tt-3', score: '' },
    ],
    planTargetId: 'plan-1',
    phaseTargetId: 'sp-1',
    annualId: '115',
    organizationId: 'org-1',
    jobTypeId: '3',
    userId: 'stu-1',
    phaseStartDate: '2026/01/01',
    phaseEndDate: '2026/03/31',
  },
  {
    phaseInstId: 'pi-2-3',
    changeable: '0',
    queryDeptType: '0',
    phaseStartDateStr: '2026/01/01',
    phaseEndDateStr: '2026/04/30',
    phaseName: '外科部',
    studentEmployeeId: 'EMP002',
    schoolId: '',
    studentName: '學員乙',
    clinicalName: '主治醫師戊',
    transcriptDetailList: [
      { transcriptTargetId: 'tt-1', score: '90' },
      { transcriptTargetId: 'tt-2', score: '88' },
      { transcriptTargetId: 'tt-3', score: '92' },
    ],
    planTargetId: 'plan-1',
    phaseTargetId: 'sp-1',
    annualId: '115',
    organizationId: 'org-1',
    jobTypeId: '3',
    userId: 'stu-2',
    phaseStartDate: '2026/01/01',
    phaseEndDate: '2026/04/30',
  },
  {
    phaseInstId: 'pi-3-2',
    changeable: '1',
    queryDeptType: '0',
    phaseStartDateStr: '2025/07/01',
    phaseEndDateStr: '2025/09/30',
    phaseName: '急診',
    studentEmployeeId: 'EMP003',
    schoolId: 'S003',
    studentName: '學員丙',
    clinicalName: '',
    transcriptDetailList: [],
    planTargetId: 'plan-2',
    phaseTargetId: 'sp-1',
    annualId: '115',
    organizationId: 'org-1',
    jobTypeId: '3',
    userId: 'stu-3',
    phaseStartDate: '2025/07/01',
    phaseEndDate: '2025/09/30',
  },
];

// ---------------------------------------------------------------------------
// 批次 15 Wave 3（B7-3）：教師安排（training-arrange-train）種子（getTrainArrangeListAjax；
// arrangeTrain.jsp:104-214 14 欄＋修改 modal 用 mentorAmount/clinicalAmount）。
// 【雛形值待實機校準】
// ---------------------------------------------------------------------------

export const ARRANGE_TRAIN_SEED: readonly Row[] = [
  {
    phaseStartDate: '2026/01/01',
    phaseEndDate: '2026/03/31',
    phaseDepartmentName: '內科部',
    parent1PhaseType: 'Group',
    parent1Name: '第一組',
    parent2PhaseType: 'Phase',
    parent2Name: '內科部',
    phaseTargetName: '門診學習（一）',
    phaseName2: '內科',
    studentName: '學員甲',
    studentEmployeeId: 'EMP001',
    officeMobile: '0912000001',
    officeCode: 'A1',
    clinicalIdName: '住院醫師丁',
    clinicalId2Name: '主治醫師戊',
    clinicalId3Name: '',
    mentor1Name: '教師甲',
    mentor2Name: '',
    locationName: '內科病房',
    formDone: '2',
    formTodo: '6',
    formDonePercentage: '33',
    isActive: '1',
    clinicalId: 'c-1',
    clinicalId2: 'c-2',
    clinicalId3: '',
    mentorId1: 't-1',
    mentorId2: '',
    wardId: 'w-1',
    clinicalAmount: '2',
    mentorAmount: '1',
    annualId: '115',
    organizationId: 'org-1',
    jobTypeId: '3',
    planTargetId: 'plan-1',
    phaseTargetId: 'sp-1',
    studentId: 'stu-1',
  },
  {
    phaseStartDate: '2026/01/01',
    phaseEndDate: '2026/04/30',
    phaseDepartmentName: '外科部',
    parent1PhaseType: 'Phase',
    parent1Name: '外科部',
    parent2PhaseType: '',
    parent2Name: '',
    phaseTargetName: '病房學習（一）',
    phaseName2: '',
    studentName: '學員乙',
    studentEmployeeId: 'EMP002',
    officeMobile: '0912000002',
    officeCode: 'A2',
    clinicalIdName: '主治醫師戊',
    clinicalId2Name: '',
    clinicalId3Name: '',
    mentor1Name: '教師乙',
    mentor2Name: '教師丙',
    locationName: '外科病房',
    formDone: '1',
    formTodo: '3',
    formDonePercentage: '33',
    isActive: '1',
    clinicalId: 'c-2',
    clinicalId2: '',
    clinicalId3: '',
    mentorId1: 't-2',
    mentorId2: 't-3',
    wardId: 'w-2',
    clinicalAmount: '1',
    mentorAmount: '2',
    annualId: '115',
    organizationId: 'org-1',
    jobTypeId: '3',
    planTargetId: 'plan-1',
    phaseTargetId: 'sp-2',
    studentId: 'stu-2',
  },
  {
    phaseStartDate: '2025/07/01',
    phaseEndDate: '2025/09/30',
    phaseDepartmentName: '急診',
    parent1PhaseType: 'Phase',
    parent1Name: '急診',
    parent2PhaseType: '',
    parent2Name: '',
    phaseTargetName: '急診學習',
    phaseName2: '',
    studentName: '學員丙',
    studentEmployeeId: 'EMP003',
    officeMobile: '',
    officeCode: '',
    clinicalIdName: '',
    clinicalId2Name: '',
    clinicalId3Name: '',
    mentor1Name: '',
    mentor2Name: '',
    locationName: '',
    isActive: '0',
    clinicalId: '',
    clinicalId2: '',
    clinicalId3: '',
    mentorId1: '',
    mentorId2: '',
    wardId: '',
    clinicalAmount: '0',
    mentorAmount: '0',
    annualId: '115',
    organizationId: 'org-1',
    jobTypeId: '3',
    planTargetId: 'plan-1',
    phaseTargetId: 'sp-3',
    studentId: 'stu-3',
  },
];

// ---------------------------------------------------------------------------
// 批次 15 Wave 3（B7-4，issue #13）：訓練計畫（instruction-plan-target-plan-target-list）種子。
// planTargetList_js.jsp:17-119：學年度/醫院/計畫執行科部/計畫名稱(name→editInstrunctionPlan
// 連結)/訓練計畫(plan.name＝periodStr)/學員名冊(studentListBtn)。【雛形值待實機校準】
// ---------------------------------------------------------------------------

export const PLAN_TARGET_SEED: readonly Row[] = [
  {
    date: '115',
    unit: '三軍總醫院',
    unit2: '內科部',
    title: '一般醫學訓練計畫',
    title2: '2026/01/01~2026/12/31',
    annualIdName: '115',
    organizationIdName: '三軍總醫院',
    departmentIdName: '內科部',
    name: '一般醫學訓練計畫',
    periodStr: '2026/01/01~2026/12/31',
    planTargetId: 'plan-1',
  },
  {
    date: '115',
    unit: '內湖院區',
    unit2: '外科部',
    title: '住院醫師訓練計畫',
    title2: '2026/01/01~2026/12/31',
    annualIdName: '115',
    organizationIdName: '內湖院區',
    departmentIdName: '外科部',
    name: '住院醫師訓練計畫',
    periodStr: '2026/01/01~2026/12/31',
    planTargetId: 'plan-2',
  },
  {
    date: '114',
    unit: '三軍總醫院',
    unit2: '急診醫學部',
    title: '急診醫學訓練計畫',
    title2: '2025/01/01~2025/12/31',
    annualIdName: '114',
    organizationIdName: '三軍總醫院',
    departmentIdName: '急診醫學部',
    name: '急診醫學訓練計畫',
    periodStr: '2025/01/01~2025/12/31',
    planTargetId: 'plan-3',
  },
];

/**
 * 簽核流程列種子（templateForFiller.jsp：產表單/填寫選擇的 formFlowListTable；
 * legacy 由 preparePhaseFormAjax/getStudyTargetListForFillAjax 回，【雛形值待實機校準】）。
 * Student 列鎖定本人；Mentor 列帶科部＋帳號 select。
 */
export const TRAINING_FORM_FLOW_SEED: readonly Row[] = [
  {
    flowRole: 'Student',
    flowRoleName: '學員',
    accountId: 'stu-1',
    accountName: '學員甲',
    filler1Id: '',
    filler1Name: '',
  },
  {
    flowRole: 'Mentor',
    flowRoleName: '導師',
    accountId: 't-1',
    accountName: '教師甲',
    filler1Id: '',
    filler1Name: '',
  },
];

// ---------------------------------------------------------------------------
// 批次 10（B1-2）：帳號大 modal 的子資料種子與 select 選項池（AccountV2DialogModal 同源）。
// profileList＝getAllAccountListAjax 列上的部門職稱（deptpartment.jsp:49 currentRow.profileList）；
// certifications＝config/certification/getCertificationListAjax 的 list（存列上鏡像）。
// 選項池為【雛形值待實機校準】（legacy 由 setJobTypeElement 等 ajax 填充，deptpartment.jsp:11-34）。
// ---------------------------------------------------------------------------

/** 醫院（spring organization＝醫院；legacy mainOrganizationList）。 */
export const ACCOUNT_ORGANIZATION_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'org-1', label: '三軍總醫院' },
  { value: 'org-2', label: '內湖院區' },
  { value: 'org-3', label: '汀州院區' },
  { value: 'org-4', label: '基隆分院' },
];

/** 訓練類別（spring job.type；legacy jobType 清單）。 */
export const ACCOUNT_JOB_TYPE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'jt-1', label: '住院醫師' },
  { value: 'jt-2', label: 'PGY' },
  { value: 'jt-3', label: '研修醫師' },
];

/** 部門（spring department；legacy setAllDepartmentElementByOrganization）。 */
export const ACCOUNT_DEPARTMENT_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'dept-1', label: '內科部' },
  { value: 'dept-2', label: '外科部' },
  { value: 'dept-3', label: '護理部' },
];

/** 批次 15（B7-1）：階段導師/臨床教師的科部下拉（phaseMentorDepts／searchMentorDept；同池）。 */
export const TRAINING_MENTOR_DEPT_OPTIONS = ACCOUNT_DEPARTMENT_OPTIONS;

/** 職稱（spring job.title；legacy jobTitle 清單）。 */
export const ACCOUNT_JOB_TITLE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'title-1', label: '主治醫師' },
  { value: 'title-2', label: '住院醫師' },
  { value: 'title-3', label: '護理長' },
  { value: 'title-4', label: '藥師' },
];

/** 辦公室位址（other.jsp:230 officeLocation；legacy setLocationElement）。 */
export const ACCOUNT_OFFICE_LOCATION_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'loc-1', label: '內湖院區' },
  { value: 'loc-2', label: '汀州院區' },
  { value: 'loc-3', label: '基隆分院' },
];

/** 證照類別（legacy getCertificationListAjax 回的 certificateTypeList；雛形種子【雛形值待實機校準】）。 */
export const ACCOUNT_CERTIFICATE_TYPE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '醫師證書', label: '醫師證書' },
  { value: '專科醫師證書', label: '專科醫師證書' },
  { value: '護理師證書', label: '護理師證書' },
  { value: '藥師證書', label: '藥師證書' },
];

// ---------------------------------------------------------------------------
// 批次 16（B8-2，Gitea issue #15）：個人基本資料維護（profile-profile-maintenance）種子。
// 來源＝profile/profileMaintenance.jsp＋ProfileController.java＋raw DOM capture。
// ---------------------------------------------------------------------------

/** 證照類別（profileMaintenanceTab_certification_modal.jsp:26-33；選項由 showList 的
 * certificateTypeList 動態重填；raw DOM profile-profile-maintenance.html:1875 捕捉到 6 選項
 * ＝1 執業登記/2 專科證書1/3 專科證書2/4 急救證書/5 其他證書/6 兵役證明）。
 * 帳號大 modal 的 ACCOUNT_CERTIFICATE_TYPE_OPTIONS 是 accountV2 capture 的另一組，不共用。
 */
export const PROFILE_CERTIFICATE_TYPE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '1', label: '執業登記' },
  { value: '2', label: '專科證書1' },
  { value: '3', label: '專科證書2' },
  { value: '4', label: '急救證書' },
  { value: '5', label: '其他證書' },
  { value: '6', label: '兵役證明' },
];

/**
 * 個人資料 readonly 身分欄種子（profileMaintenance.jsp:111-184 的 server 值；雛形值待實機校準）。
 * 0001855（ProfileController.java:96-98）：醫院別/姓名/員工編號/所屬單位/登入帳號/學號UGY/
 * 電子信箱(公務)/行動電話(公務)/工作地點號碼/簡碼/工作地點分機 不可維護（後端 :102/:107-108/:115-116
 * 全被註解）→ 這些欄雛形 readonly＋預填種子（批次 15 transcript 查詢欄預填先例）。
 * employeeId/schoolId 在 JSP 無 name 屬性（:136/:142）→ 不進 FormData，不進 C5-h 分母。
 */
export const PROFILE_IDENTITY_SEED: Row = {
  organizationName: '三軍總醫院',
  departmentName: '內科部',
  name: '醫師甲',
  loginName: 'admin',
  employeeId: 'A123456789',
  schoolId: 'S1001',
  email1: 'admin@tsgh.example.com',
  officeMobile: '0912000000',
  officeCode: '1000',
  appCode: 'APP1000',
  officeTel: '02-7777777',
  extNo: '1234',
};

/**
 * 部門職稱種子（掛第 1 列；legacy profileList entry 欄位照 template 用鍵：
 * deptpartment.jsp:196-208 的 id/organizationName/jobTypeName/departmentName/jobTitleName
 * ＋回填用 id 欄（:62-69）。permissionProfileRoleList 預勾第 1 個角色（roleId 對 config-role
 * 列鍵；roleName 存檔時由角色清單回填，種子留空字串【雛形值待實機校準】）。
 */
export const ACCOUNT_DEPT_PROFILE_SEED: Row = {
  id: 'profile-9001',
  organizationId: 'org-1',
  organizationName: '三軍總醫院',
  jobTypeId: 'jt-1',
  jobTypeName: '住院醫師',
  departmentId: 'dept-1',
  departmentName: '內科部',
  jobTitleId: 'title-1',
  jobTitleName: '主治醫師',
  permissionProfileRoleList: [{ roleId: 'CON-114-0001', roleName: '' }],
};

/** 證照種子（掛第 1 列；legacy certification 列欄位照 certification.jsp:11-31 的用鍵）。 */
export const ACCOUNT_CERTIFICATION_SEED: Row = {
  id: 'cert-9101',
  sort: 1,
  erasable: 1,
  type: '專科醫師證書',
  name: '內科專科醫師證書',
  regisDateString: '2020/03/01',
  expireDateString: '2028/02/29',
  photoFrontFileId: '',
  photoBackFileId: '',
  memo: '',
};

/**
 * course-clazz-list 種子配方（開課管理，clazzList.jsp）。點名/報名等進階資料
 * （attendance、announcements）於 Stage C 補；此處先給課程時間種子（2026/09 分散）
 * 供行事曆檢視分組。【雛形值待實機校準】
 */
const COURSE_CLAZZ_LIST_SEED: readonly Row[] = [
  // 批次 22（B2-2）：行事曆事件欄位（title/name、start/end=classBegin/EndDateTimeString、
  // kind 色 class→#E5B12E/meeting→#348DF6，clazzList_js.jsp:11-12/:267-293）。
  // 日期【雛形值待實機校準】：legacy 由 getClassListAjax 逐筆帶回；格式＝fullCalendar
  // 可解析字串，雛形以 'YYYY-MM-DD HH:mm' 鏡像（LightCalendar 切片用）。
  { title: '急救訓練課程', date: '2026/09/05', kind: 'class', classBeginDateTimeString: '2026-09-05 09:00', classEndDateTimeString: '2026-09-05 12:00' },
  { title: '感染管制課程', date: '2026/09/12', kind: 'class', classBeginDateTimeString: '2026-09-12 09:00', classEndDateTimeString: '2026-09-12 12:00' },
  { title: '醫學倫理課程', date: '2026/09/19', kind: 'class', classBeginDateTimeString: '2026-09-19 13:00', classEndDateTimeString: '2026-09-19 16:00' },
  { title: '病歷寫作課程', date: '2026/09/26', kind: 'class', classBeginDateTimeString: '2026-09-26 09:00', classEndDateTimeString: '2026-09-26 12:00' },
  { title: '實地操作訓練', date: '2026/09/05', kind: 'class', classBeginDateTimeString: '2026-09-05 14:00', classEndDateTimeString: '2026-09-05 16:00' },
  { title: '臨床技能工作坊', date: '2026/09/12', kind: 'meeting', classBeginDateTimeString: '2026-09-12 14:00', classEndDateTimeString: '2026-09-12 16:00' },
];

/**
 * 批次 11（B2-1）：課程大 modal 選項池（【雛形值待實機校準】：legacy 由 ajax 級聯填充，
 * clazzList_js.jsp:758-1611 initInputForm；雛形簡化為平列，醫院/訓練類別/部門沿用帳號池同源）。
 * actionType 十項照 JSP literal（clazzList.jsp:582-593）；kind 預設 'class'（clazzList_js.jsp:546-548）。
 */
export const COURSE_CLAZZ_KIND_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'class', label: '課程' },
];

export const COURSE_CLAZZ_CASE_TYPE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '1', label: '必修' },
  { value: '2', label: '選修' },
];

export const COURSE_CLAZZ_ACTION_TYPE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '1', label: '紀錄單' },
  { value: '2', label: '口頭測驗' },
  { value: '3', label: '筆試測驗' },
  { value: '4', label: '實地操作' },
  { value: '5', label: '書面報告' },
  { value: '6', label: '心得報告' },
  { value: '7', label: '附件上傳' },
  { value: '8', label: '實例討論' },
  { value: '9', label: '臨床評估表單' },
  { value: '10', label: '線上測驗' },
];

export const COURSE_CLAZZ_DEVICE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'dev-1', label: '電腦教室' },
  { value: 'dev-2', label: '投影機' },
  { value: 'dev-3', label: '模擬教室' },
];

export const COURSE_CLAZZ_LOCATION_DEPT_OPTIONS = ACCOUNT_DEPARTMENT_OPTIONS;

export const COURSE_CLAZZ_LOCATION_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'loc-1', label: '內湖院區會議室A' },
  { value: 'loc-2', label: '汀州院區會議室B' },
  { value: 'loc-3', label: '基隆分院教室C' },
];

export const COURSE_CLAZZ_TEACHER_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 't-1', label: '教師甲' },
  { value: 't-2', label: '教師乙' },
  { value: 't-3', label: '教師丙' },
];

/** 學員雙 select 左欄（可挑選池；legacy 由 queryStudentList 填充，:1815-1861）。 */
export const COURSE_CLAZZ_STUDENT_POOL: readonly { value: string; label: string }[] = [
  { value: 'stu-1', label: '學員甲' },
  { value: 'stu-2', label: '學員乙' },
  { value: 'stu-3', label: '學員丙' },
  { value: 'stu-4', label: '學員丁' },
  { value: 'stu-5', label: '學員戊' },
];

export const COURSE_CLAZZ_COURSE_TYPE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'ct-1', label: '急救訓練' },
  { value: 'ct-2', label: '感染管制' },
  { value: 'ct-3', label: '醫學倫理' },
];

export const COURSE_CLAZZ_TEMPLATE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'tpl-1', label: '急救訓練樣版' },
  { value: 'tpl-2', label: '感染管制樣版' },
  { value: 'tpl-3', label: '醫學倫理樣版' },
];

export const COURSE_CLAZZ_ANNUAL_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '114', label: '114' },
  { value: '115', label: '115' },
];

export const COURSE_CLAZZ_FORM_TEMPLATE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'ft-1', label: '課後問卷' },
  { value: 'ft-2', label: '訓練回饋表' },
  { value: 'ft-3', label: '實地操作評值表' },
];

export const COURSE_CLAZZ_PLAN_TARGET_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'plan-1', label: '一般醫學訓練計畫' },
  { value: 'plan-2', label: '住院醫師訓練計畫' },
];

export const COURSE_CLAZZ_PHASE_OPTIONS = ACCOUNT_DEPARTMENT_OPTIONS;

/**
 * 課程大 modal 子資料種子（掛第 1 列）：attendanceList＝學員雙 select 右欄回填（
 * clazzList_js.jsp:587-603 attendanceList→student_to）、classTeacherList＝教師清單（:605-614）、
 * attachFiles＝教材表（:618-651 initAttachFileList）、classForms＝表單控制區（:2102-2143
 * getClassFormTargetListAjax）。【雛形值待實機校準】
 */
export const COURSE_CLAZZ_SUBDATA_SEED: Row = {
  kind: 'class',
  caseType: '1',
  classDate: '2026/09/12',
  beginTime: '08:00',
  endTime: '12:00',
  isSignUp: '1',
  signUpPeriod: '2026/08/01 - 2026/09/10',
  attendanceQty: '3',
  qty: '4',
  visitingInstructor: '',
  attendanceList: [
    { id: 'att-1', studentId: 'stu-1', studentName: '學員甲', employeeId: 'EMP001' },
    { id: 'att-2', studentId: 'stu-2', studentName: '學員乙', employeeId: 'EMP002' },
  ],
  classTeacherList: [{ teacherId: 't-1', teacherName: '教師甲', type: 1 }],
  attachFiles: [{ fileId: 'file-1', name: '急救訓練講義.pdf', size: '1024 KB' }],
  classForms: [
    {
      formTargetId: 'target-1',
      formTemplateId: 'ft-1',
      formName: '課後問卷',
      doneDateDiff: 3,
      dispatchType: 1,
      isParticipantRead: 0,
      formList: [{ studentId: 'stu-1', enable: true }],
    },
  ],
};

/**
 * course-clazz-query-list 種子配方（課程查詢）。attendance／rollCallCode／forms／
 * announcements 存在課程列本身（寫入走 update()）。學生名單與表單為雛形種子。
 * 【雛形值待實機校準】
 */
const QUERY_STUDENTS: readonly Row[] = [
  { studentId: 'S1', name: '學員甲', status: '未點名', score: '', reason: '' },
  { studentId: 'S2', name: '學員乙', status: '到課', score: '88', reason: '' },
  { studentId: 'S3', name: '學員丙', status: '未到課', score: '', reason: '請假' },
  { studentId: 'S4', name: '學員丁', status: '未點名', score: '', reason: '' },
];

const COURSE_QUERY_SEED: readonly Row[] = [
  {
    attendance: QUERY_STUDENTS,
    rollCallCode: '',
    // 批次 46（FRM-001，Gitea issue #43）：forms 補 showFill 導航帶參（clazzQueryFormModal.jsp:71-76
    // → formList.js:113-122）；formTemplateId/formTemplateVersionId/refTable/refId 【雛形值待實機校準】。
    forms: [
      {
        student: '學員甲',
        name: '急救訓練檢核表',
        dueDate: '2026/09/30',
        flow: '待填寫',
        status: '未完成',
        version: '1',
        formKey: 'fk-cq-1',
        formTemplateId: 'ft-cq-1',
        formTemplateVersionId: 'ftv-cq-1',
        refTable: 'StudyRecordForm',
        refId: 'pt-cq-1',
      },
      {
        student: '學員乙',
        name: '急救訓練檢核表',
        dueDate: '2026/09/30',
        flow: '已繳交',
        status: '已完成',
        version: '1',
        formKey: 'fk-cq-2',
        formTemplateId: 'ft-cq-2',
        formTemplateVersionId: 'ftv-cq-2',
        refTable: 'StudyRecordForm',
        refId: 'pt-cq-2',
      },
    ],
    announcements: [{ subject: '上課教室更改為B棟201', date: '2026/01/05', status: '一般' }],
    rollCallCompleted: '1',
    // 批次 22（B2-2）：行事曆事件欄位【雛形值待實機校準】（1 月＝fixtures DATE_EPOCH 月）。
    kind: 'class',
    classBeginDateTimeString: '2026-01-06 09:00',
    classEndDateTimeString: '2026-01-06 12:00',
  },
  {
    attendance: QUERY_STUDENTS,
    rollCallCode: '123456',
    forms: [],
    announcements: [],
    rollCallCompleted: '1',
    kind: 'class',
    classBeginDateTimeString: '2026-01-13 14:00',
    classEndDateTimeString: '2026-01-13 16:00',
  },
  {
    // 批次 11（B2-6）：首頁模式的可見列（開放報名＋日期在固定今天起一個月窗內；
    // 窗＝2026/01/05~2026/02/04，fixtures DATE_EPOCH）。
    attendance: QUERY_STUDENTS,
    rollCallCode: '',
    forms: [],
    announcements: [],
    isSignUp: '1',
    date: '2026/01/20',
    kind: 'class',
    classBeginDateTimeString: '2026-01-20 09:00',
    classEndDateTimeString: '2026-01-20 12:00',
  },
];

const COURSE_MANAGE_SEED: readonly Row[] = [
  { category: '必修', category2: '西醫UGY', unit: '教學部', category3: '急救訓練', title: '急救訓練樣版A', score: 1, status: '啟用', erasable: '1', usageUsed: '0' },
  { category: '選修', category2: '西醫UGY', unit: '內科部', category3: '醫學倫理', title: '醫學倫理樣版B', score: 2, status: '啟用', erasable: '1', usageUsed: '1' },
  { category: '必修', category2: 'PGY', unit: '教學部', category3: '感染管制', title: '感染管制樣版C', score: 3, status: '啟用', erasable: '1', usageUsed: '0' },
  { category: '選修', category2: '西醫UGY', unit: '外科部', category3: '', title: '病歷寫作樣版D', score: 4, status: '停用', erasable: '1', usageUsed: '0' },
];

// ---------------------------------------------------------------------------
// 批次 17（B8-3，Gitea issue #16）：投票維護＋customer 設定頁種子。
// 來源＝vote/voteMaintenance.js＋modal 三 JSP＋VoteController.java＋
// highRiskSetting.jsp/serviceStandardSetting.jsp＋TsghCustomerFuncController.java。
// ---------------------------------------------------------------------------

/**
 * 投票清單三態種子（voteMaintenance.js:317-366 col0/col7 分支：enable 1 啟用→刪除鈕＋停用鈕／
 * 0 停用→已停用＋啟用鈕／-1 已刪除→已刪除）。organizationId/annualId 帶值
 * （matchesFilters 精確比對）；annualYear＝民國（annualDisp=0）。【雛形值待實機校準】
 */
const VOTE_SEED: readonly Row[] = [
  {
    organizationId: 'org-1',
    annualId: '115',
    annualYear: '115',
    name: '114 年度優良臨床教師投票',
    voteDuration: '2026/01/01~2026/01/31',
    dataDuration: '2025/10/01~2025/12/31',
    enable: 1,
  },
  {
    organizationId: 'org-1',
    annualId: '115',
    annualYear: '115',
    name: '113 年度優良臨床教師投票',
    voteDuration: '2025/01/01~2025/01/31',
    dataDuration: '2024/10/01~2024/12/31',
    enable: 0,
  },
  {
    organizationId: 'org-1',
    annualId: '114',
    annualYear: '114',
    name: '112 年度優良臨床教師投票',
    voteDuration: '2024/01/01~2024/01/31',
    dataDuration: '2023/10/01~2023/12/31',
    enable: -1,
  },
];

/**
 * 批次 21（#24）：單位主管推薦清單種子（vote/list 同源；deptManagerRecommend.js:305-338
 * initDataTable 消費端：id/annualYear/name/voteDuration/excellentTeacherRecommended；
 * excellentTeacherRecommended 0/1 混合＝投票/檢視鈕分支（:326-332）；V002＝已推薦（僅檢視）。
 * voteStartDate/voteEndDate＝投票期間日期過濾種子（D21-8）。【雛形值待實機校準】
 */
const RECOMMEND_VOTE_SEED: readonly Row[] = [
  {
    id: 'V001',
    organizationId: 'org-1',
    annualId: '115',
    annualYear: '115',
    name: '115 年度優良臨床教師投票',
    voteDuration: '2026/01/01~2026/01/31',
    voteStartDate: '2026/01/01',
    voteEndDate: '2026/01/31',
    excellentTeacherRecommended: 0,
  },
  {
    id: 'V002',
    organizationId: 'org-1',
    annualId: '115',
    annualYear: '115',
    name: '114 年度優良臨床教師投票',
    voteDuration: '2025/01/01~2025/01/31',
    voteStartDate: '2025/01/01',
    voteEndDate: '2025/01/31',
    excellentTeacherRecommended: 1,
  },
  {
    id: 'V003',
    organizationId: 'org-1',
    annualId: '114',
    annualYear: '114',
    name: '113 年度優良臨床教師投票',
    voteDuration: '2024/01/01~2024/01/31',
    voteStartDate: '2024/01/01',
    voteEndDate: '2024/01/31',
    excellentTeacherRecommended: 0,
  },
];

/**
 * 批次 21（#24）：優良教師投票名單種子（vote/getExcellentTeacherData；VoteController.java:163-198
 * 名單 dedup＋科部排序；excellentTeacher.js renderAttendingPhysicians/renderResidents 消費端：
 * id/departmentName/name/expansion1（debugMode 來源）。maxVote＝ceil(len/2)（:102/:127）：
 * 主治 2 員→1 票、住院 3 員→2 票。voted 旗標＝送出後再訪 redirect:/main 的鏡像（D21-2）。
 * 【雛形值待實機校準】
 */
const EXCELLENT_TEACHER_SEED: readonly Row[] = [
  { id: 'T001', departmentName: '內科部', name: '教師甲', expansion1: 'UGY 主治', voted: 0, teacherCategory: 'ATTENDING_PHYSICIAN' },
  { id: 'T002', departmentName: '外科部', name: '教師乙', expansion1: 'UGY 主治', voted: 0, teacherCategory: 'ATTENDING_PHYSICIAN' },
  { id: 'T003', departmentName: '急診醫學部', name: '醫師丙', expansion1: 'UGY 住院', voted: 0, teacherCategory: 'RESIDENT' },
  { id: 'T004', departmentName: '神經內科部', name: '醫師丁', expansion1: 'UGY 住院', voted: 0, teacherCategory: 'RESIDENT' },
  { id: 'T005', departmentName: '復健醫學部', name: '醫師戊', expansion1: 'UGY 住院', voted: 0, teacherCategory: 'RESIDENT' },
];

/**
 * 高風險設定可編輯表種子（getHighRiskDeptSettingListAjax 的 list，settingType=2；
 * itemCode 0＝檢查項目／1＝手術項目；highRiskSetting.jsp:53-96 欄位形狀）。
 * 【雛形值待實機校準】
 */
const HIGH_RISK_SETTING_SEED: readonly Row[] = [
  {
    id: '1',
    departmentId: 'dept-1',
    settingType: '2',
    itemCode: '0',
    itemValue3: '1',
    itemDesc1: '檢查項目範例 A',
    itemValue1: '5',
    itemValue2: '10',
  },
  {
    id: '2',
    departmentId: 'dept-1',
    settingType: '2',
    itemCode: '0',
    itemValue3: '2',
    itemDesc1: '檢查項目範例 B',
    itemValue1: '3',
    itemValue2: '6',
  },
  {
    id: '3',
    departmentId: 'dept-1',
    settingType: '2',
    itemCode: '1',
    itemValue3: '1',
    itemDesc1: '手術項目範例 A',
    itemValue1: '2',
    itemValue2: '4',
  },
];

/**
 * 合理醫療服務量固定評估項目（server settingList＝queryTsghServiceStandardSetting，
 * settingType=0；serviceStandardSetting.jsp:69-108 欄位形狀：itemDesc1 評估項目＋
 * itemDesc2 單位 addon）。【雛形值待實機校準】
 */
export const SERVICE_STANDARD_ITEMS: readonly Row[] = [
  { id: '1', itemCode: '1', itemDesc1: '評估項目範例一', itemDesc2: '人次', sort: '1' },
  { id: '2', itemCode: '2', itemDesc1: '評估項目範例二', itemDesc2: '床次', sort: '2' },
  { id: '3', itemCode: '3', itemDesc1: '評估項目範例三', itemDesc2: '班次', sort: '3' },
];

/**
 * 批次 22（B2-2，Gitea issue #7）：課程行事曆群的日期範圍過濾。legacy 查詢表單的
 * beginDate/endDate（期間）由 server 端過濾（ClassUtils.queryClass →
 * course/clazz/getClassListAjax；首頁模式 tsghfunc/getClassListForHomepageAjax），
 * 雛形以 classBeginDateTimeString 的日期部分落在 [begin, end] 鏡像【雛形值待實機校準】。
 * 回傳 undefined＝此 resource 無日期範圍語意（其餘 140 頁照舊全等比對）。
 * 無 classBeginDateTimeString 的列（泛用列）不被日期過濾（誠實標註：mock 無上課日期＝無從判斷）。
 */
/**
 * 批次 23（#25）：form/formTemplate 餘頁種子群【雛形值待實機校準】。
 * allFormList.jsp:76-133 欄定義／formStaticList.jsp:34-90／commonFormTemplate.jsp:25-53／
 * formTemplateList.jsp:39-124（行號見各 Body 檔頭）。
 */
/** 綜合表單查詢（form-all-form-list）。formKey/formTemplateId 等供列點擊導航（showFillForm）。 */
const FORM_ALL_LIST_SEED: readonly Row[] = [
  {
    formName: '急救訓練檢核表', purpose: 'plan', purposeName: '訓練計畫',
    planName: '115-1 住院醫師訓練計畫', phaseName: '第一階段/一般病房', classDate: '',
    departmentName: '教學部', studentName: '學員甲', submitDate: '2026-09-06', isDone: '1',
    formKey: 'fk-1', formTemplateId: 'ft-1', formTemplateVersionId: 'ftv-1',
    fttRefTable: 'TrainingPlan', fttRefId: 'plan-1', phaseTargetId: 'pt-1',
  },
  {
    formName: '課後問卷', purpose: 'class', purposeName: '課程',
    planName: '115-1 急救訓練課程', phaseName: '115-1 急救訓練課程', classDate: '2026-09-05',
    departmentName: '急診醫學部', studentName: '學員乙', submitDate: '2026-09-07', isDone: '0',
    formKey: 'fk-2', formTemplateId: 'ft-2', formTemplateVersionId: 'ftv-2',
    fttRefTable: 'Clazz', fttRefId: 'cou-114-0001', phaseTargetId: 'pt-2',
  },
  {
    // studyItem 列：列點擊走 showStudyRecordFill（allFormList.jsp:139-143）。
    formName: '學習項目自評表', purpose: 'studyItem', purposeName: '學習項目',
    planName: '115-1 住院醫師訓練計畫', phaseName: '第二階段/加護病房', classDate: '',
    departmentName: '教學部', studentName: '學員丙', submitDate: '', isDone: '0',
    formKey: 'fk-3', formTemplateId: 'ft-3', formTemplateVersionId: 'ftv-3',
    fttRefTable: 'StudyRecordForm', fttRefId: 'srf-1', phaseTargetId: 'pt-3', formId: 'srf-1',
  },
  {
    formName: '臨床技能評估表', purpose: 'plan', purposeName: '訓練計畫',
    planName: '115-1 藥師訓練計畫', phaseName: '第一階段/門診', classDate: '',
    departmentName: '藥劑部', studentName: '學員丁', submitDate: '2026-09-10', isDone: '1',
    formKey: 'fk-4', formTemplateId: 'ft-4', formTemplateVersionId: 'ftv-4',
    fttRefTable: 'TrainingPlan', fttRefId: 'plan-2', phaseTargetId: 'pt-4',
  },
];

/**
 * 共同編輯查詢（form-co-edit-form-query-page）種子。欄 key 實算（批次 46，FRM-002）：
 * legacy table headers（契約 11 欄，第一欄空＝select-all）經 columnsFromHeaders 推斷：
 * title=表單名稱／unit=訓練科部／date=課程日期／date2=表單發送日期／endDate=應完成日期／
 * person=主筆者／text=表單流程／person2=共同編輯人員／status=表單狀態／text2=表單版本。
 * formKey＝名稱連結導航（queryPage.js:104-108 → CoEditFormUtils.js:48-50）；
 * formTargetTypeName＝名稱第二行（queryPage.js:208-211）。值【雛形值待實機校準】。
 * 批次 52（§6.3 裁定）：本頁表格列入 29 頁空表化（EMPTY_TABLE_RESOURCES）——凍結/live
 * 均 0 列，種子列不可見；fk-ce-1~3 formKey 導航路徑自本批起為不可達（stub 測試仍可直接
 * 導航驗證），下批若重灌 demo 資料再議。
 */
const CO_EDIT_QUERY_SEED: readonly Row[] = [
  {
    title: '共同編輯教案檢核表',
    formKey: 'fk-ce-1',
    formTargetTypeName: '醫師',
    unit: '教學部',
    date: '2026/01/06',
    date2: '2026/01/05',
    endDate: '2026/01/20',
    person: '醫師甲',
    text: '待填寫',
    person2: '醫師乙、護理師甲',
    status: '進行中',
    text2: '1',
  },
  {
    title: '共同編輯病摘回饋單',
    formKey: 'fk-ce-2',
    formTargetTypeName: '護理師',
    unit: '內科部',
    date: '2026/01/08',
    date2: '2026/01/07',
    endDate: '2026/01/22',
    person: '醫師乙',
    text: '共同編輯中',
    person2: '護理師乙',
    status: '未開始',
    text2: '1',
  },
  {
    title: '共同編輯出院備妥單',
    formKey: 'fk-ce-3',
    formTargetTypeName: 'PGY',
    unit: '急診醫學部',
    date: '2026/01/12',
    date2: '2026/01/11',
    endDate: '2026/01/26',
    person: '醫師丙',
    text: '已繳交',
    person2: '醫師甲、藥師甲',
    status: '已完成',
    text2: '2',
  },
];

/** 表單統計（form-form-static-list）。無列互動（formStaticList.jsp:34-90 無 tbody click）。 */
const FORM_STATIC_LIST_SEED: readonly Row[] = [
  { startYM: '2026-09', purposeName: '訓練計畫', courseTypeName: '', formName: '急救訓練檢核表', departmentName: '教學部', formNum: '12', formFinishNum: '9' },
  { startYM: '2026-09', purposeName: '課程', courseTypeName: '急救訓練', formName: '課後問卷', departmentName: '急診醫學部', formNum: '30', formFinishNum: '21' },
  { startYM: '2026-08', purposeName: '課程', courseTypeName: '感染管制', formName: '感染管制回饋單', departmentName: '內科部', formNum: '8', formFinishNum: '8' },
  { startYM: '2026-08', purposeName: '訓練計畫', courseTypeName: '', formName: '臨床技能評估表', departmentName: '藥劑部', formNum: '5', formFinishNum: '3' },
];

/** 開課統計（course-course-static-list，批次 24 #26）。無列互動（courseStaticList.jsp:44-96 無 tbody click）；
 *  場次/總時數 null→'0'（:73-95）由 Body renderCell 鏡像；kind＝CourseStaticViewModel 顯示值【雛形值待實機校準】。 */
const COURSE_STATIC_LIST_SEED: readonly Row[] = [
  { kind: '課程', courseTypeName: '急救訓練', courseYM: '2026-09', departmentName: '教學部', courseNums: '4', courseHourNum: '8' },
  { kind: '課程', courseTypeName: '急救訓練', courseYM: '2026-09', departmentName: '急診醫學部', courseNums: '2', courseHourNum: '4' },
  { kind: '課程', courseTypeName: '感染管制', courseYM: '2026-08', departmentName: '內科部', courseNums: '3', courseHourNum: '6' },
  { kind: '會議', courseTypeName: '晨會', courseYM: '2026-08', departmentName: '教學部', courseNums: '5', courseHourNum: '2.5' },
  { kind: '課程', courseTypeName: '緊照應變', courseYM: '2026-09', departmentName: '藥劑部', courseNums: null, courseHourNum: null },
];

/** 共用表單設定（form-template-common-form-template）。欄：刪除鈕/表單名稱/用途/來源 3 欄。 */
export const COMMON_FORM_TEMPLATE_SEED: readonly Row[] = [
  { formTemplateName: '急救訓練檢核表', purpose: 'plan', srcOrgShortName: '總院', srcJobTypeName: '西醫', srcDeptName: '教學部' },
  { formTemplateName: '課後問卷', purpose: 'class', srcOrgShortName: '總院', srcJobTypeName: '西醫', srcDeptName: '急診醫學部' },
  { formTemplateName: '實地操作評分表', purpose: 'class', srcOrgShortName: '總院', srcJobTypeName: '西醫', srcDeptName: '護理部' },
  { formTemplateName: '學習項目自評表', purpose: 'plan', srcOrgShortName: '總院', srcJobTypeName: '西醫', srcDeptName: '教學部' },
];

/** 表單設計（form-template-form-template-list）。審核四態＋頁型鈕＋有新版待審查（:97-122）。 */
export const FORM_TEMPLATE_LIST_SEED: readonly Row[] = [
  // id 補種子值（新增共用表單 modal 的來源表單池以 id 為 option value；enrich 合併同值覆寫）。
  { id: 'FOR-114-0001', jobTypeName: '西醫', departmentName: '教學部', name: '急救訓練檢核表', purpose: 'plan', enable: '1', verify: '', verifyerIsMe: '0', unverifyVersionCount: '0', sort: '1' },
  { id: 'FOR-114-0002', jobTypeName: '西醫', departmentName: '急診醫學部', name: '課後問卷', purpose: 'class', enable: '1', verify: 'NOT', verifyerIsMe: '1', unverifyVersionCount: '0', sort: '2' },
  { id: 'FOR-114-0003', jobTypeName: '西醫', departmentName: '護理部', name: '實地操作評分表', purpose: 'class', enable: '0', verify: 'FAIL', verifyerIsMe: '0', unverifyVersionCount: '0', sort: '3' },
  { id: 'FOR-114-0004', jobTypeName: '西醫', departmentName: '內科部', name: '出院病摘摘要單', purpose: 'general', enable: '1', verify: 'PASS', verifyerIsMe: '0', unverifyVersionCount: '0', sort: '4' },
  { id: 'FOR-114-0005', jobTypeName: '西醫', departmentName: '教學部', name: '學習項目自評表', purpose: 'studyItem', enable: '1', verify: 'NOT', verifyerIsMe: '1', unverifyVersionCount: '2', sort: '5' },
];

/**
 * 批次 23（#25）：查詢過濾為 server 端語意的頁群（queryFormListAjax/getCommonFormTemplateListAjax/
 * getFormTemplateDetailListAjax/queryFormStaticListAjax）——雛形種子直出（B19-6「種子直出」先例），
 * 查詢欄只渲染不過濾；C5-h wiring 照送 filters（complexBodySubmitId）。
 */
const QUERY_PASSTHROUGH_RESOURCES: ReadonlySet<string> = new Set([
  'form-all-form-list',
  'form-form-static-list',
  'form-template-common-form-template',
  'form-template-form-template-list',
  'form-plan-form-batch-send',
  'form-template-field-type-list',
  // 批次 24（#26）：開課統計（queryCourseStaticListAjax，CourseController.java:566
  // ClassInCourse queryCriteria server 端過濾）同族。
  'course-course-static-list',
  // 批次 26（#21）：config 其餘 7 頁查詢皆 server 端過濾（getQueryCommentListAjax
  // comment.js:266/getDeviceListAjax device.jsp:139/getJobTitleListAjax jobTitle.jsp:104/
  // getJobTypeListAjax jobType.jsp:226/getJobTypeByOrgIdAjax jobTypeDepartment.jsp:161/
  // getOrganizationListAjax organization.jsp:124；forbidWord ready 即查無參數不進本集合）。
  'config-comment',
  'config-device',
  'config-job-title',
  'config-job-type',
  'config-job-type-department',
  'config-organization',
]);

export function resourceQueryPassthrough(resource: string): boolean {
  return QUERY_PASSTHROUGH_RESOURCES.has(resource);
}

/** 批次 23 Wave 2（#25）：批次發送表單種子（planFormBatchSend.jsp getFormStudentListAjax 回應）【雛形值待實機校準】。 */
export const BATCH_SEND_STUDENTS: readonly Row[] = [
  { studentId: 'S1', planTargetName: '115-1 住院醫師訓練計畫', phaseStartDateString: '2026-01-01', phaseEndDateString: '2026-06-30', studentName: '學員甲' },
  { studentId: 'S2', planTargetName: '115-1 住院醫師訓練計畫', phaseStartDateString: '2026-01-01', phaseEndDateString: '2026-06-30', studentName: '學員乙' },
  { studentId: 'S3', planTargetName: '115-1 藥師訓練計畫', phaseStartDateString: '2026-02-01', phaseEndDateString: '2026-07-31', studentName: '學員丙' },
];

/** 表單版本流程（formTemplateVersion.flowList；第一關＝非學員以涵蓋『必須指定第一關卡之流程人員!』分支）。 */
export const BATCH_SEND_FLOW_VERSION: {
  formTemplateVersion: { flowList: readonly { id: string; flowRole: string; roleName: string; sequence: string }[] };
} = {
  formTemplateVersion: {
    flowList: [
      { id: 'fw-1', flowRole: 'Clinical', roleName: '臨床教師', sequence: '1' },
      { id: 'fw-2', flowRole: 'Student', roleName: '學員', sequence: '2' },
      { id: 'fw-3', flowRole: 'DeptManager', roleName: '部門主管', sequence: '3' },
    ],
  },
};

/** 訓練計畫表單選項（formTemplateId option data-all formTargetTypeTag；Form360 分支涵蓋）【雛形值待實機校準】。 */
export const BATCH_SEND_TEMPLATE_OPTIONS: readonly { value: string; label: string; formTargetTypeTag: string }[] = [
  { value: 'FOR-114-0001', label: '急救訓練檢核表', formTargetTypeTag: 'Flow' },
  { value: 'FOR-114-0002', label: '360度評核表', formTargetTypeTag: 'Form360' },
];

/** 表單精靈表頭用途選項（formTemplate.jsp purpose select；用途同 formTemplateList.jsp:329-339）。 */
export const FORM_TEMPLATE_PURPOSE_BODY_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'plan', label: '訓練計畫' },
  { value: 'class', label: '課程' },
  { value: 'general', label: '一般表單' },
  { value: 'studyItem', label: '學習項目' },
  { value: 'co-editor-form', label: '共同編輯' },
];

/** 批次 23 Wave 3（#25）：表單精靈版本列表種子（formTemplate.jsp formTable 欄 :63-124）【雛形值待實機校準】。 */
export const FORM_TEMPLATE_VERSION_SEED: readonly Row[] = [
  { id: 'FTV-1', version: '1', annualYear: '115', startDateString: '2026-01-05', flowSummary: '學員→臨床教師→部門主管', verify: 'PASS' },
  { id: 'FTV-2', version: '2', annualYear: '115', startDateString: '2026-01-20', flowSummary: '學員→臨床教師→部門主管', verify: '' },
];

/** 批次 23 Wave 3（#25）：常用題型格式維護種子（fieldTypeList.jsp 表 3 欄＋題型編輯欄位子集）【雛形值待實機校準】。 */
export const FIELD_TYPE_SEED: readonly Row[] = [
  { id: 'FTY-1', jobTypeName: '西醫', name: '單行文字', sampleTitle: '請輸入說明', subjectStyle: '', format: '', grade: '' },
  { id: 'FTY-2', jobTypeName: '西醫', name: '單選題', sampleTitle: '請選擇下列選項', subjectStyle: '', format: '', grade: '' },
  { id: 'FTY-3', jobTypeName: '西醫', name: '評分量表', sampleTitle: '請評分', subjectStyle: '', format: '', grade: '' },
];

/** 批次 25（#27）：登入頁公告種子（index.jsp＋indexAnnounce.js getAnnouncements；
 *  raw-dom login/index.html:114 兩則——測試帳號說明(2026-09-25)／測試環境公告：本站為 legacy
 *  系統的還原環境(2026-09-25)；endDate/refName/roleNames/url/content raw-dom 未捕獲（modal
 *  未開啟）【雛形值待實機校準】）。整表替換（非逐列合併）：公告欄必須恰好這兩則；
 *  rows.length===0（empty scenario）維持空清單。 */
export const INDEX_ANNOUNCEMENT_SEED: readonly Row[] = [
  {
    id: '2',
    subject: '測試帳號說明',
    startDateStr: '2026-09-25',
    endDateStr: '2026-12-31',
    refName: '教學部',
    roleNames: '全部角色',
    url: '',
    content: 'ui-admin／ui-teacher／ui-student 三個測試帳號（密碼同帳號）可供示範登入；各角色首頁與權限內容不同。',
  },
  {
    id: '1',
    subject: '測試環境公告：本站為 legacy 系統的還原環境',
    startDateStr: '2026-09-25',
    endDateStr: '2026-12-31',
    refName: '系統管理員',
    roleNames: '全部角色',
    url: '',
    content: '本站為 legacy 系統的還原環境，資料為示範用種子，請勿輸入真實病患或個資。',
  },
];

/** 批次 25（#27）：公告附件種子（login/getMyAttachFileListAjax；raw-dom 未捕獲
 *  【雛形值待實機校準】；測試帳號說明附 1 檔＝涵蓋附件表/下載分支，另一則 0 檔＝fileListDiv 隱藏分支）。 */
export const INDEX_ANNOUNCEMENT_ATTACH_SEED: Readonly<Record<string, readonly Row[]>> = {
  '2': [{ id: 'AF-2-1', name: '測試帳號清單.txt', size: '128', fileId: 'af-2-1' }],
};

/** 批次 26（#21）：config 其餘 7 頁種子群。行為事實表（偵察報告）以 device.jsp/jobTitle.jsp/
 *  comment.jsp＋comment.js/forbidWord.jsp/jobType.jsp/jobTypeDepartment.jsp/organization.jsp 為準；
 *  列值除 raw-dom 可見者外【雛形值待實機校準】。第 0 列一律帶 usageUsed='1'（usage check 分支）。
 *  erasable＝權限模型無（D20-2/D23-5）→ 種子旗標等價。 */

/** 設備維護（config-device）。欄：unit 醫院/unit2 管轄部門（'0'＝共用，render :29-34）/category 設備類別碼
 *  （cellOverride 映射 constant.deviceType :36-38）/id 設備代碼/title 名稱/text 照片占位/score 排序/status。 */
const DEVICE_SEED: readonly Row[] = [
  { unit: '總院', unit2: '0', category: '0', id: 'DEV-001', title: 'CPR 安妮', text: '', score: '1', status: '啟用', erasable: '1', usageUsed: '1' },
  { unit: '總院', unit2: '教學部', category: '1', id: 'DEV-002', title: '縫合練習組', text: '', score: '2', status: '啟用', erasable: '1', usageUsed: '0' },
  { unit: '總院', unit2: '急診醫學部', category: '2', id: 'DEV-003', title: '電動病床', text: '', score: '3', status: '啟用', erasable: '1', usageUsed: '0' },
  { unit: '總院', unit2: '護理部', category: '3', id: 'DEV-004', title: '點滴架', text: '', score: '4', status: '停用', erasable: '1', usageUsed: '0' },
];

/** 職稱維護（config-job-title）。欄：id 職稱代碼（row.id＝code）/text 職稱/score 排序/status。
 *  批次 52（§3.1/§3.2；裁定 B）：4→12 列逐字（§3.1 live 真值 getJobTitleListAjax，凍結同值 ✓；
 *  已按 sort asc 排 63→91）；泛用池列取消（SEED 覆蓋滿 12 列，SEED_ROW_COUNTS 同步 12）；
 *  每列補 organizationId:'1'（live ajax 有此欄）；erasable/status 逐列照表（啟用才渲染刪除）。 */
const JOB_TITLE_SEED: readonly Row[] = [
  { id: 'JT-001', text: '住院醫師', score: '1', status: '啟用', erasable: '1', usageUsed: '1', organizationId: '1' },
  { id: 'JT-002', text: 'PGY', score: '2', status: '啟用', erasable: '1', usageUsed: '0', organizationId: '1' },
  { id: 'JT-003', text: '實習醫學生', score: '3', status: '啟用', erasable: '1', usageUsed: '0', organizationId: '1' },
  { id: 'JT-004', text: '臨床教師', score: '4', status: '停用', erasable: '1', usageUsed: '0', organizationId: '1' },
  { id: 'CON-114-0012', text: '期中口試評分表，適用於婦產部，由住院醫師乙維護。', score: '63', status: '停用', erasable: '1', organizationId: '1' },
  { id: 'CON-114-0007', text: '住院醫師甄選作業，適用於麻醉部，由學員甲維護。', score: '68', status: '停用', erasable: '1', organizationId: '1' },
  { id: 'CON-114-0009', text: '學習護照認證項目，適用於教學部，由學員丙維護。', score: '69', status: '啟用', erasable: '1', organizationId: '1' },
  { id: 'CON-114-0005', text: '急救訓練課程，適用於放射線部，由學員甲維護。', score: '70', status: '啟用', erasable: '1', organizationId: '1' },
  { id: 'CON-114-0011', text: '學習護照認證項目，適用於護理部，由教師甲維護。', score: '83', status: '啟用', erasable: '1', organizationId: '1' },
  { id: 'CON-114-0008', text: '期中口試評分表，適用於婦產部，由學員戊維護。', score: '88', status: '停用', erasable: '1', organizationId: '1' },
  { id: 'CON-114-0010', text: '急救訓練課程，適用於急診醫學部，由教師乙維護。', score: '88', status: '停用', erasable: '1', organizationId: '1' },
  { id: 'CON-114-0006', text: '實證醫學讀書會，適用於教學部，由學員丙維護。', score: '91', status: '停用', erasable: '1', organizationId: '1' },
];

/** 常用語維護（config-comment）。欄：category 訓練類別/unit 科部/category2 使用範圍/title 常用語標題
 *  （showEdit）/text 常用語內容/category3 類別（typeName【雛形值待實機校準】）/score 排序/status；
 *  deletable＝本人或 sysAdmin 才可刪/停（comment.js:85-87/:122-133）→種子旗標等價（D26-1）。 */
const COMMENT_SEED: readonly Row[] = [
  { category: '西醫', unit: '教學部', category2: '訓練計畫', title: '出院病摘常用語', text: '病人情況穩定，按計畫出院。', category3: '常用語', score: '1', status: '啟用', deletable: '1', usageUsed: '0' },
  { category: '西醫', unit: '內科部', category2: '課程', title: '課程通知常用語', text: '課程時間異動，請註意。', category3: '常用語', score: '2', status: '啟用', deletable: '1', usageUsed: '0' },
  { category: '西醫', unit: '急診醫學部', category2: '訓練計畫', title: '急診轉介常用語', text: '依流程轉介。', category3: '提醒', score: '3', status: '停用', deletable: '1', usageUsed: '0' },
  { category: '西醫', unit: '護理部', category2: '課程', title: '他人維護常用語', text: '非本人維護（無刪除/啟停鈕）。', category3: '提醒', score: '4', status: '啟用', deletable: '0', usageUsed: '0' },
];

/** 訓練類別設定（config-job-type）。欄：category 訓練類別名稱/score 排序/status（啟停/已刪除）。 */
const JOB_TYPE_SEED: readonly Row[] = [
  { category: '西醫', score: '1', status: '啟用', erasable: '1', usageUsed: '1' },
  { category: '西醫PGY', score: '2', status: '啟用', erasable: '1', usageUsed: '0' },
  { category: '護理', score: '3', status: '停用', erasable: '1', usageUsed: '0' },
];

/** 訓練類別部門維護（config-job-type-department）。欄：unit 醫院/category 訓練類別/score 排序序號；
 *  departmentsTo（穿梭 To 池）＋admin/manager 科部＝編輯 modal 帶值（:108-135）【雛形值待實機校準】。 */
const JOB_TYPE_DEPARTMENT_SEED: readonly Row[] = [
  { unit: '總院', category: '西醫', score: '1', orgId: 'org-1', jobTypeId: '1', departmentsTo: ['內科部', '外科部', '急診醫學部'], adminDepartment: '教學部', managerDepartment: '教學部' },
  { unit: '總院', category: '西醫PGY', score: '2', orgId: 'org-1', jobTypeId: '2', departmentsTo: ['家庭醫學部'], adminDepartment: '教學部', managerDepartment: '教學部' },
  { unit: '分院', category: '西醫', score: '3', orgId: 'org-2', jobTypeId: '1', departmentsTo: ['內科部'], adminDepartment: '教學部', managerDepartment: '教學部' },
];

/** 組織維護（config-organization）。欄：unit 組織類型（raw main/community，cellOverride 映射）/id 組織代碼
 *  （row.id＝code）/unit2 名稱（showEdit）/unit3 簡稱/score 排序/status（啟停；第 7 權限欄 legacy 隱藏 :65-66）。 */
const ORGANIZATION_SEED: readonly Row[] = [
  { unit: 'main', id: 'LOCAL', unit2: '本機帳號', unit3: '本機', score: '0', status: '啟用', erasable: '1', usageUsed: '1' },
  { unit: 'main', id: 'ORG-MAIN', unit2: '總院', unit3: '總院', score: '1', status: '啟用', erasable: '1', usageUsed: '0' },
  { unit: 'community', id: 'ORG-EXT', unit2: '外訓醫院甲', unit3: '外訓甲', score: '2', status: '啟用', erasable: '1', usageUsed: '0' },
  { unit: 'community', id: 'ORG-EXT2', unit2: '外訓醫院乙', unit3: '外訓乙', score: '3', status: '停用', erasable: '1', usageUsed: '0' },
];

/** 禁用語維護（config-forbid-word）。欄：title 禁用語；row.id>99 才可刪（:17，前 100 筆系統種子不可刪）
 *  → 種子含 id='50'（不可刪邊界）。無查詢表單（ready 即查 :39）、無啟停。 */
const FORBID_WORD_SEED: readonly Row[] = [
  { id: '50', title: '系統內建禁用語（不可刪）' },
  { id: '101', title: '示範禁用語甲' },
  { id: '102', title: '示範禁用語乙' },
];

/**
 * 批次 52（§7.2 表格列差 H2 清單；§8 裁決 B 核准）：29 頁種子**空表化**——凍結 legacy
 * 查詢結果全為真空表（0 資料列＋「查無符合的資料」＋info 0/0），react 泛用池列＝
 * ~324 筆結構差（v3 勘誤：297 tr＋27 info），逐頁清零收斂。剔除 5 頁
 * （config-annual/config-department/config-organization/study-record-fill/status）
 * **不在**此清單（§7.2-B 維持現狀：react tr[0] 已與凍結配對，空表化＝新增缺元素）。
 * 副作用：這些頁 demo 無列；行為測試中「編輯/刪除既有列」的前置列需先建立（§8-B）。
 */
export const EMPTY_TABLE_RESOURCES: ReadonlySet<string> = new Set([
  'announcement-announcement-list',
  'form-co-edit-form-query-page',
  'config-account-v-2',
  'config-device',
  'config-forbid-word',
  'config-location',
  'course-course-manage',
  'course-course-type',
  'form-class-form-list',
  'instruction-course-department-code',
  'discuss-discuss-board',
  'discuss-discuss-type',
  'exam-dispatch-exam',
  'exam-exam-manage',
  'exam-question-bank-type',
  'exam-question-bank',
  'exam-testing-manage',
  'instruction-teacher-pool-list',
  'instruction-plan-target-plan-target-list',
  'instruction-study-item-maintain',
  'instruction-study-item-type-maintain',
  'config-job-type-department',
  'leave-leave-annual',
  'leave-leave-form',
  'leave-leave-type',
  'config-comment',
  'system-changelog',
  'form-plan-form-list',
  'training-plan-inst-plan-inst-list',
]);

/**
 * 批次 52（§7.3 反向：legacy 有真列、react 泛用列）：逐 resource 種子列數覆寫——
 * config-role＝凍結 9 實列（系統角色目錄；泛用 3 列＝多元素差）；
 * config-job-title＝§3.1 live 真值 12 列（裁定 B：種 12 列，排序/分頁交行為批）。
 * 命中後 buildRows 以此數產生泛用列骨架，再由 enrichResourceRows 逐列 merge 種子值。
 */
export const SEED_ROW_COUNTS: Readonly<Record<string, number>> = {
  'config-role': 9,
  'config-job-title': 12,
};

const DATE_RANGE_RESOURCES: ReadonlySet<string> = new Set([
  'course-clazz-list',
  'course-clazz-query-list',
  'course-clazz-query-list-class-query',
]);

export function resourceDateRangeFilter(
  resource: string,
): ((row: Row, filters: Record<string, string>) => boolean) | undefined {
  if (!DATE_RANGE_RESOURCES.has(resource)) return undefined;
  return (row, filters) => {
    const begin = String(filters['beginDate'] ?? '');
    const end = String(filters['endDate'] ?? '');
    if (begin === '' && end === '') return true;
    const day = String(row['classBeginDateTimeString'] ?? '').slice(0, 10);
    if (day === '') return true;
    if (begin !== '' && day < begin) return false;
    if (end !== '' && day > end) return false;
    return true;
  };
}

/**
 * 逐 resource 補種子欄位。只在**新種子**產生時呼叫一次（persisted 資料已含補欄）。
 */
export function enrichResourceRows(resource: string, rows: readonly Row[]): Row[] {
  switch (resource) {
    case 'config-annual':
      return rows.map((row, index) => ({
        ...row,
        ...(ANNUAL_SEED[index] ?? {}),
      }));
    case 'config-department':
      // 子部門的 parentId 要引用**實際列鍵**（CON-114-00xx），種子先以 DEPT-<index> 標記，
      // 這裡換算成同一批列的 id，樹狀圖才能組出階層。
      return rows.map((row, index) => {
        const seed = DEPARTMENT_SEED[index];
        if (seed === undefined) {
          return row;
        }
        const applied: Row = { ...row, ...seed };
        if (String(applied['parentId']).startsWith('DEPT-')) {
          const parentIndex = Number(String(applied['parentId']).slice(5));
          const parent = rows[parentIndex];
          applied['parentId'] = String(parent?.id ?? '0');
        }
        return applied;
      });
    case 'config-role':
      return rows.map((row, index) => {
        const systemName = ROLE_SYSTEM_NAMES[index];
        if (systemName !== undefined) {
          // 系統角色：名稱逐字對齊 legacy；不可刪（role.jsp:32-45 `row.id>=1000`）。
          return {
            ...row,
            category: systemName,
            score: index + 1,
            sysRole: '',
            flowRole: '',
            deletable: '0',
          };
        }
        const custom = ROLE_CUSTOM_SEED[index - ROLE_SYSTEM_NAMES.length];
        return custom === undefined ? row : { ...row, ...custom, deletable: '1' };
      });
    case 'config-location':
      // 第 0 列標記已被使用＋固定啟用（usage check 兩種分支＋刪除被擋的分支都按得到）;
      // 第 1 列固定「啟用」，刪除連結的顯示條件才可確定性測試。
      return rows.map((row, index) =>
        index === 0
          ? { ...row, usageUsed: '1', status: '啟用', erasable: '1' }
          : index === 1
            ? { ...row, status: '啟用', erasable: '1' }
            : row,
      );
    case 'config-account-v-2':
      // 帳號維護：第 0 列停用（測「啟用帳號」批量）、第 1 列啟用（測「停用帳號」批量），
      // 其餘維持泛型值。勾選欄的顯示條件依 status（accountV2.jsp:19-23）。
      // 批次 10（B1-2）：第 1 列補 profileList／certifications 種子（大 modal 部門職稱/證照
      // 分頁的既有資料；存檔以 update 存回列上，RolePermissionModal 先例）。
      return rows.map((row, index) => {
        if (index === 0) {
          return { ...row, status: '停用' };
        }
        if (index === 1) {
          return {
            ...row,
            status: '啟用',
            profileList: [ACCOUNT_DEPT_PROFILE_SEED],
            certifications: [ACCOUNT_CERTIFICATION_SEED],
          };
        }
        return row;
      });
    case 'course-course-type':
      return rows.map((row, index) => ({ ...row, ...(COURSE_TYPE_SEED[index] ?? {}) }));
    case 'course-course-manage':
      return rows.map((row, index) => ({ ...row, ...(COURSE_MANAGE_SEED[index] ?? {}) }));
    case 'course-clazz-list':
      // 批次 11（B2-1）：第 1 列補大 modal 子資料種子（attendanceList→學員雙 select 右欄、
      // classTeacherList→教師清單、attachFiles→教材表、classForms→表單控制區；
      // legacy showModal 編輯回填，clazzList_js.jsp:522-600；資料存列上寫回 update op）。
      return rows.map((row, index) => ({
        ...row,
        ...(COURSE_CLAZZ_LIST_SEED[index] ?? {}),
        ...(index === 1 ? COURSE_CLAZZ_SUBDATA_SEED : {}),
      }));
    case 'course-clazz-query-list':
    case 'course-clazz-query-list-class-query':
      return rows.map((row, index) => ({ ...row, ...(COURSE_QUERY_SEED[index] ?? {}) }));
    case 'exam-question-bank':
      return rows.map((row, index) => ({ ...row, ...(QUESTION_BANK_SEED[index] ?? {}) }));
    case 'form-all-form-list':
      // 批次 23（#25）：綜合表單查詢種子（欄定義 allFormList.jsp:76-133；行點擊導航用 formKey 等）。
      return rows.map((row, index) => ({ ...row, ...(FORM_ALL_LIST_SEED[index] ?? {}) }));
    case 'form-co-edit-form-query-page':
      // 批次 46（FRM-002，Gitea issue #44）：共同編輯查詢種子（欄 key 實算見 CO_EDIT_QUERY_SEED
      // 檔頭註解；formKey/formTargetTypeName 供名稱連結導航＝queryPage.js:104-108）。
      return rows.map((row, index) => ({ ...row, ...(CO_EDIT_QUERY_SEED[index] ?? {}) }));
    case 'form-form-static-list':
      // 批次 23（#25）：表單統計種子（欄定義 formStaticList.jsp:34-90；無列互動）。
      return rows.map((row, index) => ({ ...row, ...(FORM_STATIC_LIST_SEED[index] ?? {}) }));
    case 'course-course-static-list':
      // 批次 24（#26）：開課統計種子（欄定義 courseStaticList.jsp:44-96；無列互動）。
      return rows.map((row, index) => ({ ...row, ...(COURSE_STATIC_LIST_SEED[index] ?? {}) }));
    case 'index':
      // 批次 25（#27）：登入頁公告整表替換（恰好兩則；empty scenario 維持空清單）。
      if (rows.length === 0) return rows.slice();
      return INDEX_ANNOUNCEMENT_SEED.map((row): Row => ({ ...row }));
    case 'config-comment':
      // 批次 26（#21）：常用語種子（欄定義 comment.js:83-133；deletable 旗標＝D26-1）。
      return rows.map((row, index) => ({ ...row, ...(COMMENT_SEED[index] ?? {}) }));
    case 'config-device':
      // 批次 26（#21）：設備種子（欄定義 device.jsp:18-62；unit2 '0'＝共用/category 碼）。
      return rows.map((row, index) => ({ ...row, ...(DEVICE_SEED[index] ?? {}) }));
    case 'config-forbid-word':
      // 批次 26（#21）：禁用語種子（row.id>99 才可刪 :17；50＝不可刪邊界）。
      return rows.map((row, index) => ({ ...row, ...(FORBID_WORD_SEED[index] ?? {}) }));
    case 'config-job-title':
      // 批次 26（#21）：職稱種子（欄定義 jobTitle.jsp:18-49；row.id＝職稱代碼）。
      return rows.map((row, index) => ({ ...row, ...(JOB_TITLE_SEED[index] ?? {}) }));
    case 'config-job-type':
      // 批次 26（#21）：訓練類別種子（欄定義 jobType.jsp:28-76）。
      return rows.map((row, index) => ({ ...row, ...(JOB_TYPE_SEED[index] ?? {}) }));
    case 'config-job-type-department':
      // 批次 26（#21）：訓練類別部門種子（欄定義 :16-26＋編輯 modal 帶值 :108-135）。
      return rows.map((row, index) => ({ ...row, ...(JOB_TYPE_DEPARTMENT_SEED[index] ?? {}) }));
    case 'config-organization':
      // 批次 26（#21）：組織種子（欄定義 organization.jsp:17-62；unit raw main/community； isAdmin 寫死 0＝:154）。
      return rows.map((row, index) => ({ ...row, ...(ORGANIZATION_SEED[index] ?? {}) }));
    case 'form-template-common-form-template':
      // 批次 23（#25）：共用表單設定種子（欄定義 commonFormTemplate.jsp:25-53）。
      return rows.map((row, index) => ({ ...row, ...(COMMON_FORM_TEMPLATE_SEED[index] ?? {}) }));
    case 'form-template-form-template-list':
      // 批次 23（#25）：表單設計種子（欄定義 formTemplateList.jsp:39-124；審核鈕條件用 verifyerIsMe）。
      return rows.map((row, index) => ({ ...row, ...(FORM_TEMPLATE_LIST_SEED[index] ?? {}) }));
    case 'form-plan-form-batch-send':
      // 批次 23 Wave 2（#25）：學員種子（getFormStudentListAjax 回應；list op = 學員查詢）。
      return rows.map((row, index) => ({ ...row, ...(BATCH_SEND_STUDENTS[index] ?? {}) }));
    case 'form-template-field-type-list':
      // 批次 23 Wave 3（#25）：題型種子（表 3 欄＋題型編輯欄位子集；fieldTypeList.jsp:45-67）。
      return rows.map((row, index) => ({ ...row, ...(FIELD_TYPE_SEED[index] ?? {}) }));
    case 'exam-exam-manage':
      // 批次 13（B3-1）：試卷種子（modal 用 legacy 鍵＋examGroups 子清單；存列上寫回 update op）。
      return rows.map((row, index) => ({ ...row, ...(EXAM_MANAGE_SEED[index] ?? {}) }));
    case 'exam-dispatch-exam':
      // 批次 13（B3-3）：派送清單種子（未分派×2＋已分派四態）。
      return rows.map((row, index) => ({ ...row, ...(EXAM_DISPATCH_SEED[index] ?? {}) }));
    case 'training-plan-inst-plan-inst-list':
      // 批次 15（B7-1）：訓練期程種子（主表欄＋大 modal 期程列/角色人員設定＋明細/表單明細；存列上）。
      return rows.map((row, index) => ({ ...row, ...(TRAINING_PLAN_INST_SEED[index] ?? {}) }));
    case 'training-study-record-study-record-fill':
      // 批次 15 Wave 2（B7-2）：學習紀錄填寫種子（主表欄＋學習項目/完成狀態/已填明細存列上）。
      return rows.map((row, index) => ({
        ...row,
        ...(TRAINING_STUDY_RECORD_FILL_SEED[index] ?? {}),
        studyItems: TRAINING_STUDY_RECORD_ITEMS_SEED[String((TRAINING_STUDY_RECORD_FILL_SEED[index] ?? {})['studyTargetId'] ?? '')] ?? [],
        targetDetails: TRAINING_TARGET_DETAIL_SEED[String((TRAINING_STUDY_RECORD_FILL_SEED[index] ?? {})['studyTargetId'] ?? '')] ?? [],
        studyRecordForms: TRAINING_STUDY_RECORD_FORMS_SEED[String((TRAINING_STUDY_RECORD_FILL_SEED[index] ?? {})['studyTargetId'] ?? '')] ?? [],
      }));
    case 'training-study-record-study-record-status':
      // 批次 15 Wave 2（B7-2）：學習紀錄查詢種子（同構＋訓練計畫/學員欄）。
      return rows.map((row, index) => ({
        ...row,
        ...(TRAINING_STUDY_RECORD_STATUS_SEED[index] ?? {}),
        studyItems: TRAINING_STUDY_RECORD_ITEMS_SEED[String((TRAINING_STUDY_RECORD_STATUS_SEED[index] ?? {})['studyTargetId'] ?? '')] ?? [],
        targetDetails: TRAINING_TARGET_DETAIL_SEED[String((TRAINING_STUDY_RECORD_STATUS_SEED[index] ?? {})['studyTargetId'] ?? '')] ?? [],
        studyRecordForms: TRAINING_STUDY_RECORD_FORMS_SEED[String((TRAINING_STUDY_RECORD_STATUS_SEED[index] ?? {})['studyTargetId'] ?? '')] ?? [],
      }));
    case 'training-transcript-list':
      // 批次 15 Wave 3（B7-3）：成績單種子（complexBody 用）。
      return rows.map((row, index) => ({ ...row, ...(TRANSCRIPT_RESULT_SEED[index] ?? {}) }));
    case 'training-arrange-train':
      // 批次 15 Wave 3（B7-3）：教師安排種子（complexBody 用）。
      return rows.map((row, index) => ({ ...row, ...(ARRANGE_TRAIN_SEED[index] ?? {}) }));
    case 'instruction-plan-target-plan-target-list':
      // 批次 15 Wave 3（B7-4）：訓練計畫種子（計畫名稱欄＝修改連結）。
      return rows.map((row, index) => ({ ...row, ...(PLAN_TARGET_SEED[index] ?? {}) }));
    case 'report-tsgh-login-static':
      // 批次 14 W2：查詢統計結果種子（body 只渲染帶統計鍵的列）。
      return rows.map((row, index) => ({ ...row, ...(LOGIN_STATIC_RESULT_SEED[index] ?? {}) }));
    case 'report-tsgh-class-form-static':
      return rows.map((row, index) => ({ ...row, refType: '6', ...(CLASS_FORM_STATIC_RESULT_SEED[index] ?? {}) }));
    case 'report-tsgh-class-form-static-to-class-form-7':
      return rows.map((row, index) => ({ ...row, refType: '7', ...(CLASS_FORM_STATIC_RESULT_SEED[index] ?? {}) }));
    case 'report-tsgh-plan-form-static':
      return rows.map((row, index) => ({ ...row, refType: '4', ...(PLAN_FORM_STATIC_RESULT_SEED[index] ?? {}) }));
    case 'report-tsgh-plan-form-static-to-plan-form-5':
      return rows.map((row, index) => ({ ...row, refType: '5', ...(PLAN_FORM_STATIC_RESULT_SEED[index] ?? {}) }));
    case 'report-tsgh-plan-form-static-2':
      return rows.map((row, index) => ({ ...row, refType: '8', ...(PLAN_FORM_STATIC2_RESULT_SEED[index] ?? {}) }));
    case 'report-tsgh-plan-form-static-2-to-plan-form-9':
      return rows.map((row, index) => ({ ...row, refType: '9', ...(PLAN_FORM_STATIC2_RESULT_SEED[index] ?? {}) }));
    case 'report-tsgh-plan-form-static-2-to-plan-form-10':
      return rows.map((row, index) => ({ ...row, refType: '10', ...(PLAN_FORM_STATIC2_RESULT_SEED[index] ?? {}) }));
    case 'report-tsgh-core-skill-report-by-form':
      return rows.map((row, index) => ({ ...row, ...(CORE_SKILL_REPORT_BY_FORM_SEED[index] ?? {}) }));
    case 'report-tsgh-core-skill-report-by-student':
      return rows.map((row, index) => ({ ...row, ...(CORE_SKILL_REPORT_BY_STUDENT_SEED[index] ?? {}) }));
    case 'report-tsgh-academic-mom':
      // 批次 14 W3：學術會議紀錄種子（rows 0-2＝主表 makeTable js:257-288；rows 3-5＝基準數
      // makeBaseTable js:321-341；兩種子同掛一 resource，body 各取所需）。
      {
        const momSeeds = [...MOM_RESULT_SEED, ...MOM_BASE_SEED];
        return rows.map((row, index) => ({ ...row, ...(momSeeds[index] ?? {}) }));
      }
    case 'report-tsgh-core-skill-statistic-report':
      // 批次 14 W3：核心技能認證情形種子（單列）。
      return rows.map((row, index) => ({ ...row, ...(CORE_SKILL_STATISTIC_REPORT_SEED[index] ?? {}) }));
    case 'customer-tsgh-case-report':
      // 批次 18 S2：案例報告統計種子（body 只渲染帶 minQty 鍵的列）。
      return rows.map((row, index) => ({ ...row, ...(CUSTOMER_CASE_REPORT_SEED[index] ?? {}) }));
    case 'customer-tsgh-tsgh-student-course-report':
      // 批次 18 S2：修課紀錄查詢種子（專屬鍵 govMinQty）。
      return rows.map((row, index) => ({ ...row, ...(CUSTOMER_STUDENT_COURSE_SEED[index] ?? {}) }));
    case 'customer-tsgh-core-curriculum-report':
      // 批次 18 S3：核心課程查詢種子（專屬鍵 targets；plan/month 各 2 列＋動態表頭聯集）。
      return rows.map((row, index) => ({ ...row, ...(CUSTOMER_CORE_CURRICULUM_ALL_SEED[index] ?? {}) }));
    case 'customer-tsgh-course-record-report':
      // 批次 18 S3：修課紀錄查詢種子（專屬鍵 myQty；msg＋三 modal 種子）。
      return rows.map((row, index) => ({ ...row, ...(CUSTOMER_COURSE_RECORD_SEED[index] ?? {}) }));
    case 'customer-tsgh-clert-cerk':
      // 批次 18 S4：實習證明書種子（專屬鍵 settingId；列 0＝可申請、列 1＝已申請＋雙下載）。
      return rows.map((row, index) => ({ ...row, ...(CUSTOMER_CLERT_SEED[index] ?? {}) }));
    case 'customer-tsgh-tsgh-study-history-list':
      // 批次 18 S4：學習歷程下載種子（專屬鍵 planInstId；歷程雙下載鈕）。
      return rows.map((row, index) => ({ ...row, ...(CUSTOMER_STUDY_HISTORY_LIST_SEED[index] ?? {}) }));
    case 'form-class-form-list':
    case 'form-plan-form-list':
      return rows.map((row, index) => ({ ...row, ...(FORM_LIST_SEED[index] ?? {}) }));
    case 'exam-question-manage':
      // 批次 45（#9）：題目編輯頁種子（questionManage.jsp 題目列表；題型/難度/狀態混合態）。
      return rows.map((row, index) => ({ ...row, ...(QUESTION_LEVEL_SEED[index] ?? {}) }));
    case 'leave-leave-type':
      return rows.map((row, index) => ({ ...row, ...(LEAVE_TYPE_SEED[index] ?? {}) }));
    case 'leave-leave-form':
      // leave-fix（2026-10-04）：表單狀態輪替（legacy leaveFormStatus 顯示值）。
      return rows.map((row, index) => ({ ...row, ...(LEAVE_FORM_SEED[index] ?? {}) }));
    case 'discuss-discuss-board':
      return rows.map((row, index) => ({ ...row, ...(DISCUSS_BOARD_SEED[index] ?? {}) }));
    case 'discuss-discuss-type':
      return rows.map((row, index) => ({ ...row, ...(DISCUSS_TYPE_SEED[index] ?? {}) }));
    case 'vote-vote-maintenance':
      // 批次 17（B8-3）：投票清單三態種子（enable 1 啟用／0 停用／-1 已刪除；
      // voteMaintenance.js:317-366 col0/col7 分支全可測）；organizationId/annualId 帶值
      // （matchesFilters 精確比對＝種子必帶查詢鍵值，批次 15 教訓）。【雛形值待實機校準】
      return rows.map((row, index) => ({ ...row, ...(VOTE_SEED[index] ?? {}) }));
    case 'vote-dept-manager-recommend':
      // 批次 21（#24）：單位主管推薦清單種子（vote/list 同源；deptManagerRecommend.js:305-338
      // initDataTable 消費端：id/annualYear/name/voteDuration/excellentTeacherRecommended；
      // 0/1 混合＝投票/檢視鈕分支；annualId/organizationId 帶值＝matchesFilters）。【雛形值待實機校準】
      return rows.map((row, index) => ({ ...row, ...(RECOMMEND_VOTE_SEED[index] ?? {}) }));
    case 'vote-excellent-teacher':
      // 批次 21（#24）：優良教師投票名單（vote/getExcellentTeacherData；excellentTeacher.js:99-146
      // renderAttendingPhysicians/renderResidents 消費端：id/departmentName/name/expansion1；
      // teacherCategory＝ATTENDING_PHYSICIAN/RESIDENT（getVoteResult :148-173）；voted 旗標＝
      // 送出後 criteria 清除→再訪 redirect:/main 的鏡像。【雛形值待實機校準】
      return rows.map((row, index) => ({ ...row, ...(EXCELLENT_TEACHER_SEED[index] ?? {}) }));
    case 'vote-total-vote-report':
    case 'vote-total-vote-analysis-report':
      // 批次 21（#24）：報表頁的投票名稱下拉（totalVoteReport.js:31-40 setVoteList → vote/list
      // 填 v.id/v.name；legacy bug 照實：org 讀 select[name=orgId] 不存在＝不過濾，org →只 by 年度。
      // 種子帶 annualId＝matchesFilters）。【雛形值待實機校準】
      return rows.map((row, index) => ({ ...row, ...(RECOMMEND_VOTE_SEED[index] ?? {}) }));
    case 'customer-tsgh-high-risk-setting':
      // 批次 17（B8-3）：高風險設定可編輯表種子（getHighRiskDeptSettingListAjax 的 list；
      // settingType=2；itemCode 0 檢查項目/1 手術項目）。【雛形值待實機校準】
      return rows.map((row, index) => ({ ...row, ...(HIGH_RISK_SETTING_SEED[index] ?? {}) }));
    default:
      return [...rows];
  }
}

// ---------------------------------------------------------------------------
// 寫入驗證（validate）
// ---------------------------------------------------------------------------

export interface ResourceValidationResult {
  /** 'error'：整頁 alert（無欄位可掛）；'field'：欄位級錯誤。 */
  readonly kind: 'error' | 'field';
  readonly message: string;
  readonly field?: string;
}

/**
 * 寫入前的業務驗證。`mode`：create／update；`rows` 為資料層目前全部列
 * （重複檢查用；update 時呼叫端已把自身排除在 `excludeId` 之外）。
 */
export function validateResourceWrite(
  resource: string,
  values: Row,
  mode: 'create' | 'update',
  rows: readonly Row[],
  excludeId?: string,
): ResourceValidationResult | undefined {
  if (resource === 'config-role') {
    // role.jsp:184-193：同名角色檢查——「新增失敗，角色重複!」「修改失敗，角色重複!」。
    const name = String(values['category'] ?? '').trim();
    if (name !== '') {
      const duplicated = rows.some(
        (row) => String(row['category'] ?? '').trim() === name && String(row.id) !== excludeId,
      );
      if (duplicated) {
        return {
          kind: 'error',
          message: mode === 'create' ? '新增失敗，角色重複!' : '修改失敗，角色重複!',
        };
      }
    }
  }
  if (resource === 'course-course-type' && 'category' in values) {
    // courseType.jsp:264：jobTypeId==-1 → '請選擇訓練類別!'。
    if (String(values['category'] ?? '').trim() === '') {
      return { kind: 'field', message: '請選擇訓練類別!', field: 'category' };
    }
    // courseType.jsp:268：修改時所屬類別不得選自己。
    if (mode === 'update' && excludeId !== undefined) {
      const self = rows.find((row) => String(row.id) === excludeId);
      const parent = String(values['category3'] ?? '').trim();
      if (
        parent !== '' &&
        self !== undefined &&
        parent === String(self['category4'] ?? '').trim()
      ) {
        return { kind: 'error', message: '所屬類別不得選擇自己!' };
      }
    }
  }
  if (resource === 'config-annual' && 'date' in values) {
    // annual.jsp:148-150 save() rules：`year: {required: true, digits: true}`；
    // annual.jsp:12-22 ADYearMin/ADYearMax validator 文字：'請輸入西元年'（範圍 2000-9999，:444）。
    // 只驗「有帶年度欄」的寫入——設為預設年度（{status:'是'}）這類單欄更新不帶年度。
    const year = String(values['date'] ?? '').trim();
    if (year === '' || !/^\d+$/.test(year) || Number(year) < 2000 || Number(year) > 9999) {
      return { kind: 'field', message: '請輸入西元年', field: 'date' };
    }
  }
  // 批次 26（#21）：config 其餘 7 頁 modal 存檔驗證鏈（欄位鍵＝表欄 key；
  // legacy jquery.validate required 的文案為瀏覽器/套件產生，雛形以欄位級訊息等價，LoginPage 先例）。
  if (resource === 'config-device') {
    // device.jsp save() :160-242：code/classDevice required（:165-170）→
    // 請選擇醫院（:176）→請選擇管轄部門（:180）→請選擇設備類別（:184）。
    if (String(values['title'] ?? '').trim() === '') {
      return { kind: 'field', message: '此欄位為必填欄位', field: 'title' };
    }
    if (String(values['unit'] ?? '').trim() === '') {
      return { kind: 'field', message: '請選擇醫院', field: 'unit' };
    }
    if (String(values['unit2'] ?? '').trim() === '') {
      return { kind: 'field', message: '請選擇管轄部門', field: 'unit2' };
    }
    if (String(values['category'] ?? '').trim() === '') {
      return { kind: 'field', message: '請選擇設備類別', field: 'category' };
    }
  }
  if (resource === 'config-job-title') {
    // jobTitle.jsp save() :121-170：sort required+digits（:126-131）／name required／code required
    // （save() validate rules :124-131 明列 code）／請選擇醫院（:142）。row.id＝職稱代碼；
    // crud-fix（P35 反轉）：id 欄解除唯讀（showModal :12/:23 新增/修改皆可填 code），
    // 未填＝required 欄位錯誤（原「id 欄唯讀＝模板語意，D26-4」僅適用未解除唯讀的頁）。
    if (String(values['text'] ?? '').trim() === '') {
      return { kind: 'field', message: '此欄位為必填欄位', field: 'text' };
    }
    if (String(values['id'] ?? '').trim() === '') {
      return { kind: 'field', message: '此欄位為必填欄位', field: 'id' };
    }
    const sort = String(values['score'] ?? '').trim();
    if (sort !== '' && !/^\d+$/.test(sort)) {
      return { kind: 'field', message: '請輸入數字', field: 'score' };
    }
    if (String(values['organizationId'] ?? '').trim() === '') {
      return { kind: 'field', message: '請選擇醫院', field: 'organizationId' };
    }
  }
  if (resource === 'config-comment') {
    // comment.js save() :283-304：dialogForm.valid()（name/content/sort required，:284-287）→
    // 請選擇科部!（:288）→請選擇類別!（:292）→請選擇使用範圍!（:296）；
    // useType==451→請選擇共用角色!（:299）＝roleLimit 欄不鏡像（D26-3）；單訊息模型＝順序差異（D26-5 同族）。
    if (String(values['title'] ?? '').trim() === '') {
      return { kind: 'field', message: '此欄位為必填欄位', field: 'title' };
    }
    if (String(values['text'] ?? '').trim() === '') {
      return { kind: 'field', message: '此欄位為必填欄位', field: 'text' };
    }
    if (String(values['unit'] ?? '').trim() === '') {
      return { kind: 'field', message: '請選擇科部!', field: 'unit' };
    }
    if (String(values['category3'] ?? '').trim() === '') {
      return { kind: 'field', message: '請選擇類別!', field: 'category3' };
    }
    if (String(values['category2'] ?? '').trim() === '') {
      return { kind: 'field', message: '請選擇使用範圍!', field: 'category2' };
    }
  }
  if (resource === 'config-forbid-word') {
    // forbidWord.jsp save() :80-115：isEmpty→禁用語不可為空值!（:82）。
    if (String(values['title'] ?? '').trim() === '') {
      return { kind: 'field', message: '禁用語不可為空值!', field: 'title' };
    }
  }
  if (resource === 'config-job-type') {
    // jobType.jsp save() errorMsg 累加式（:243-267）：首項＝訓練類別名稱不可為空值!（:243，
    // spring:message job.type＝訓練類別）；累加後一次顯示＝mock 單訊息取首項（D26-5）。
    if (String(values['category'] ?? '').trim() === '') {
      return { kind: 'field', message: '訓練類別名稱不可為空值!', field: 'category' };
    }
  }
  if (resource === 'config-organization') {
    // organization.jsp save() :138-175：dialogForm.valid()（type/code/name required）；
    // isAdmin 寫死 0（:154）＝D26-6。
    if (String(values['unit'] ?? '').trim() === '') {
      return { kind: 'field', message: '此欄位為必填欄位', field: 'unit' };
    }
    if (String(values['unit2'] ?? '').trim() === '') {
      return { kind: 'field', message: '此欄位為必填欄位', field: 'unit2' };
    }
  }
  if (resource === 'config-job-type-department') {
    // jobTypeDepartment.jsp save() :174-227：表內重複（同 orgId+jobTypeId 且 idTemp<0）→
    // 修改失敗，訓練類別部門重複!（:183-184）；selectedDepartmentIds 空→修改失敗，請選擇參與部門（:194-195）。
    const org = String(values['unit'] ?? '').trim();
    const jobType = String(values['category'] ?? '').trim();
    if (mode === 'create' && org !== '' && jobType !== '') {
      const duplicated = rows.some(
        (row) => String(row['unit'] ?? '').trim() === org && String(row['category'] ?? '').trim() === jobType,
      );
      if (duplicated) {
        return { kind: 'error', message: '修改失敗，訓練類別部門重複!' };
      }
    }
    const departments = String(values['departmentsTo'] ?? '').trim();
    if (departments === '') {
      return { kind: 'field', message: '修改失敗，請選擇參與部門', field: 'departmentsTo' };
    }
  }

  return undefined;
}

// ---------------------------------------------------------------------------
// 寫入後副作用（afterWrite）
// ---------------------------------------------------------------------------

/**
 * 寫入成功後的資料層副作用（就地修改 `rows`）。
 * - config-annual：某列 `status`（是否預設）存成「是」時，其餘列一律改「否」——
 *   預設年度全站唯一（annual.jsp setDefaultAnnualAjax／saveAnnualAjax 的 isDefault 語意）。
 * - config-role：權限 id 陣列正規化（去重）。
 */
// ---------------------------------------------------------------------------
// 批次 18（Gitea issue #23）：customer 報表/查詢頁群 13 頁的選項池與查詢結果種子。
// 級聯一律平列池（legacy 逐層 AJAX 填充 → mock 不逐層重算，批次 15 D2／批次 17 先例，登記）。
// ---------------------------------------------------------------------------

/**
 * tch-satisfaction-survey-report 的訓練類別池（callBackfunc1 過濾後只剩這三項；
 * tchSatisfactionSurveyReport.jsp ready→setJobTypeElement 回呼 :1370-1381 移除其他 option）。
 * value 為雛形值【雛形值待實機校準】；doExport 的「中醫PGY」檢核用 label 文字（:1443-1447）。
 */
export const CUSTOMER_TCH_JOB_TYPE_OPTIONS: readonly { value: string; label: string }[] = [
  // 順序照 mock 平列池自由值（legacy options 由 AJAX 填充，raw DOM 為空無順序事實）；
  // 中醫PGY 不放最後（C5-h drive 取最後一個非空 option，避免與 teacherType='3' 的
  // 「中醫PGY 沒有 住院醫師資料.」特例驗證撞車攔截驅動送出）。
  { value: 'jt-cpgy', label: '中醫PGY' },
  { value: 'jt-dugy', label: '牙醫UGY' },
  { value: 'jt-ugy', label: '西醫UGY' },
];

/**
 * tsgh-study-history-list 的訓練類別池。value '2'＝西醫PGY（doExport :1509 檢核
 * `jobTypeId!=='2'` →『匯出 計畫學員於該計畫中的平均成績、排名、百分比 [限 西醫PGY]!』），
 * 與 REPORT_JOB_TYPE_PGY 對齊才可測成功路徑。
 */
export const CUSTOMER_STUDY_HISTORY_JOB_TYPE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '1', label: '西醫UGY' },
  { value: '2', label: '西醫PGY' },
  { value: '5', label: '牙醫住院醫師' },
];

/** tsgh-study-history-list 的訓練計畫池（doExport :1513 檢核 '-1'＝「全部」選項，
 *  需選定訓練計畫才可匯出）。 */
export const CUSTOMER_STUDY_HISTORY_PLAN_TARGET_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '-1', label: '全部' },
  ...COURSE_CLAZZ_PLAN_TARGET_OPTIONS,
];

// ---------------------------------------------------------------------------
// 批次 18 S2：查詢型結果表種子（掛各頁 resource 列上；body 只渲染帶專屬鍵的列，
// 批次 14 W2 同款）。【雛形值待實機校準】legacy 由 getCaseReportAjax/
// getStudentCourseReportAjax 端點計算。
// ---------------------------------------------------------------------------

/** 案例報告統計（caseReport.jsp doQuery :1456-1485；表頭 raw :1341 六欄 generated）。
 *  專屬鍵＝minQty（fixtures 通用列沒有）。第一欄 render＝employeeId+'/'+schoolId（:1371-1373）。
 *  查詢鍵值照 body 預設值（organizationId/annualId/planTargetId/phaseDepartmentId；
 *  matchesFilters 精確比對＝種子必帶非空查詢鍵，批次 15/17 教訓）。 */
export const CUSTOMER_CASE_REPORT_SEED: readonly Row[] = [
  { employeeId: 'EMP001', schoolId: 'S001', studentName: '學員甲', formTargetTypeName: '病例報告', minQty: '3', doneQty: '2', pass: '未通過', organizationId: 'org-1', annualId: '115', planTargetId: 'plan-1', phaseDepartmentId: 'dept-1' },
  { employeeId: 'EMP002', schoolId: 'S002', studentName: '學員乙', formTargetTypeName: '病例報告', minQty: '3', doneQty: '3', pass: '通過', organizationId: 'org-1', annualId: '115', planTargetId: 'plan-1', phaseDepartmentId: 'dept-1' },
  { employeeId: 'EMP003', schoolId: 'S003', studentName: '學員丙', formTargetTypeName: '病例報告', minQty: '3', doneQty: '5', pass: '通過', organizationId: 'org-1', annualId: '115', planTargetId: 'plan-1', phaseDepartmentId: 'dept-1' },
];

/** 修課紀錄查詢（tsghStudentCourseReport.jsp doQuery :1460-1490；表頭七欄；
 *  render 特例 :1371-1392： typeName 前綴 jobType 選中文字、
 *  醫策會規定時數＝govMinQty 至 govMaxQty 小時、完成度＝totalPer% (qty/specifiedTime)）。
 *  專屬鍵＝govMinQty。jobTypeLabel＝種子側帶（第3欄 span 前綴；legacy 用 jobTypeId 選中文字）。
 *  查詢鍵值照 body 預設值（同上）。 */
export const CUSTOMER_STUDENT_COURSE_SEED: readonly Row[] = [
  { employeeId: 'EMP001', schoolId: 'S001', studentName: '學員甲', jobTypeLabel: '西醫UGY', typeName: '急症醫學', govMinQty: '24', govMaxQty: '48', specifiedTime: '40', qty: '32', totalPer: '80', organizationId: 'org-1', annualId: '115', planTargetId: 'plan-1', phaseDepartmentId: 'dept-1' },
  { employeeId: 'EMP002', schoolId: 'S002', studentName: '學員乙', jobTypeLabel: '西醫UGY', typeName: '內科學', govMinQty: '24', govMaxQty: '48', specifiedTime: '40', qty: '40', totalPer: '100', organizationId: 'org-1', annualId: '115', planTargetId: 'plan-1', phaseDepartmentId: 'dept-1' },
  { employeeId: 'EMP003', schoolId: 'S003', studentName: '學員丙', jobTypeLabel: '西醫UGY', typeName: '外科學', govMinQty: '24', govMaxQty: '48', specifiedTime: '40', qty: '28', totalPer: '70', organizationId: 'org-1', annualId: '115', planTargetId: 'plan-1', phaseDepartmentId: 'dept-1' },
];

/** 核心課程查詢（coreCurriculumReport.jsp doQuery :1512-1547；動態表頭 targets[].name＋
 *  ratio% 連結；name=='---'→'-' 照實）。專屬鍵＝targets。查詢鍵值照 body 預設值。 */
export const CUSTOMER_CORE_CURRICULUM_SEED: readonly Row[] = [
  {
    organizationId: 'org-1', annualId: '115', planTargetId: 'plan-1', resultType: 'plan',
    planTargetName: '一般醫學訓練計畫', studentName: '學員甲', employeeId: 'EMP001', schoolId: 'S001',
    startDateString: '2026/01/01', endDateString: '2026/06/30', startMonthString: '2026/01',
    targets: [
      {
        name: '核心課程A', ratio: '66.7', sumCaseQty: '6', caseQty: '4',
        tsghDetailList: [
          { studyItemName: 'A-1 基本急救', doneQty: '2', caseQty: '3', studentCourseList: [
            { learningTime: '2026/01/12', recordId: 'REC-1001' },
            { learningTime: '2026/02/09', recordId: 'REC-1002' },
          ] },
          { studyItemName: 'A-2 醫療品質', doneQty: '0', caseQty: '1', studentCourseList: [] },
        ],
      },
      { name: '---', ratio: '', sumCaseQty: '', caseQty: '', tsghDetailList: [] },
    ],
  },
  {
    organizationId: 'org-1', annualId: '115', planTargetId: 'plan-1', resultType: 'plan',
    planTargetName: '一般醫學訓練計畫', studentName: '學員乙', employeeId: 'EMP002', schoolId: 'S002',
    startDateString: '2026/01/01', endDateString: '2026/06/30', startMonthString: '2026/02',
    targets: [
      { name: '核心課程A', ratio: '100', sumCaseQty: '6', caseQty: '6', tsghDetailList: [
        { studyItemName: 'A-1 基本急救', doneQty: '3', caseQty: '3', studentCourseList: [
          { learningTime: '2026/02/15', recordId: 'REC-2001' },
        ] },
      ] },
      { name: '核心課程B', ratio: '50', sumCaseQty: '2', caseQty: '1', tsghDetailList: [
        { studyItemName: 'B-1 感染管制', doneQty: '1', caseQty: '2', studentCourseList: [
          { learningTime: '2026/02/20', recordId: 'REC-2002' },
        ] },
      ] },
    ],
  },
];

// month 模式種子（resultType radio＝month；filters 精確比對需同值列，批次 15/17 教訓）：
// 與 plan 列同學員，欄 ddtMonth＝startMonthString。
const CORE_MONTH_ROWS: readonly Row[] = [
  {
    organizationId: 'org-1', annualId: '115', planTargetId: 'plan-1', resultType: 'month', planStartDate: '2026-01-01', planEndDate: '2026-03-31',
    planTargetName: '一般醫學訓練計畫', studentName: '學員甲', employeeId: 'EMP001', schoolId: 'S001',
    startDateString: '2026/01/01', endDateString: '2026/06/30', startMonthString: '2026/01',
    targets: [
      {
        name: '核心課程A', ratio: '66.7', sumCaseQty: '6', caseQty: '4',
        tsghDetailList: [
          { studyItemName: 'A-1 基本急救', doneQty: '2', caseQty: '3', studentCourseList: [
            { learningTime: '2026/01/12', recordId: 'REC-1001' },
          ] },
        ],
      },
      { name: '---', ratio: '', sumCaseQty: '', caseQty: '', tsghDetailList: [] },
    ],
  },
  {
    organizationId: 'org-1', annualId: '115', planTargetId: 'plan-1', resultType: 'month', planStartDate: '2026-01-01', planEndDate: '2026-03-31',
    planTargetName: '一般醫學訓練計畫', studentName: '學員乙', employeeId: 'EMP002', schoolId: 'S002',
    startDateString: '2026/01/01', endDateString: '2026/06/30', startMonthString: '2026/02',
    targets: [
      { name: '核心課程A', ratio: '100', sumCaseQty: '6', caseQty: '6', tsghDetailList: [
        { studyItemName: 'A-1 基本急救', doneQty: '1', caseQty: '1', studentCourseList: [
          { learningTime: '2026/02/15', recordId: 'REC-2001' },
        ] },
      ] },
      { name: '核心課程B', ratio: '50', sumCaseQty: '2', caseQty: '1', tsghDetailList: [
        { studyItemName: 'B-1 感染管制', doneQty: '1', caseQty: '2', studentCourseList: [
          { learningTime: '2026/02/20', recordId: 'REC-2002' },
        ] },
      ] },
    ],
  },
];

export const CUSTOMER_CORE_CURRICULUM_ALL_SEED: readonly Row[] = [
  ...CUSTOMER_CORE_CURRICULUM_SEED,
  ...CORE_MONTH_ROWS,
];

/** 修課紀錄查詢（courseRecordReport.jsp doQuery :1476-1561＋三 modal 種子；
 *  專屬鍵＝myQty；msg＝後端提示照實 alert :1502-1505）。查詢鍵值照 body 預設值。 */
export const CUSTOMER_COURSE_RECORD_SEED: readonly Row[] = [
  {
    organizationId: 'org-1', annualId: '115', planTargetId: 'plan-1', phaseDepartmentId: 'dept-1',
    studentName: '學員甲', employeeId: 'EMP001', schoolId: 'S001',
    typeName: '急症醫學', tmsTypeName: 'TMS 急症', specifiedTime: '40',
    myQty: '4', tmsQty: '8', totalQty: '12', totalPer: '30',
    msg: 'TMS 課程資料同步中，部分時數可能延遲更新',
    classInCourseList: [
      { code: 'CL-101', name: '急症醫學課程一', classDate: '2026/01/12', qty: '4', score: '92', attendance: '1' },
      { code: 'CL-102', name: '急症醫學課程二', classDate: '2026/02/09', qty: '0', score: '', attendance: '0' },
    ],
    tmsCourseTypeIdMap: { '1': 'TMS 急症' },
    tmsCourseRecordList: [
      {
        coursetypeid: '1', courseid: 'TMS-100', coursename: 'ACLS 課程', coursedate: '2026/01/20',
        hour: '8', status: '1', score: '88', internal: '1', isCFD: '0',
        method: '實作測驗', assessment: '通過', resource: 'AHA 2020', research: '-',
        summary: '完成', holisticHours: '8',
      },
      {
        coursetypeid: '1', courseid: 'TMS-101', coursename: 'BLS 課程', coursedate: '2026/02/03',
        hour: '4', status: '0', score: '', internal: '0', isCFD: '0',
        method: '線上', assessment: '', resource: '-', research: '-',
        summary: '-', holisticHours: '4',
      },
    ],
  },
];

/** 實習證明書（clertCerk.jsp initListTable :1385-1425＋doApply :1463-1481）。
 *  專屬鍵＝settingId。列 0＝可申請（applyDate 空→申請鈕）；列 1＝已申請（applyDate＋雙下載鈕）。
 *  查詢鍵值照 body 預設值（organizationId/annualId/planTargetId）。 */
export const CUSTOMER_CLERT_SEED: readonly Row[] = [
  {
    organizationId: 'org-1', annualId: '115', planTargetId: 'plan-1',
    settingId: '101', studentIdName: '學員甲', annualIdName: '115',
    planTargetIdName: '一般醫學訓練計畫', applyDate: '', chCertFileId: '', enCertFileId: '',
  },
  {
    organizationId: 'org-1', annualId: '115', planTargetId: 'plan-1',
    settingId: '102', studentIdName: '學員乙', annualIdName: '115',
    planTargetIdName: '一般醫學訓練計畫', applyDate: '2026/01/15',
    chCertFileId: 'file-ch-102', enCertFileId: 'file-en-102',
  },
];

/** 學習歷程下載（tsghStudyHistoryList.jsp :1460-1491＋doQuery getPlanInstListAjax）。
 *  專屬鍵＝planInstId（歷程雙下載鈕；downloadPassport :1553-1579）。
 *  查詢鍵值照 body 預設值（organizationId/annualId/jobTypeId '1'/planTargetId '-1'）。 */
export const CUSTOMER_STUDY_HISTORY_LIST_SEED: readonly Row[] = [
  {
    organizationId: 'org-1', annualId: '115', jobTypeId: '1', planTargetId: '-1',
    planInstId: '5001', csrfToken: 'csrf-mock', annualIdName: '115', planTargetIdName: '一般醫學訓練計畫',
    studentEmployeeId: 'EMP001', studentSchoolId: 'S001', studentIdName: '學員甲',
    mentorIdName: '教師甲',
  },
  {
    organizationId: 'org-1', annualId: '115', jobTypeId: '1', planTargetId: '-1',
    planInstId: '5002', csrfToken: 'csrf-mock', annualIdName: '115', planTargetIdName: '住院醫師訓練計畫',
    studentEmployeeId: 'EMP002', studentSchoolId: 'S002', studentIdName: '學員乙',
    mentorIdName: '教師乙',
  },
];

export function applyResourceWriteSideEffects(
  resource: string,
  id: string,
  values: Row,
  rows: readonly Row[],
): void {
  if (resource === 'config-annual') {
    if (String(values['status'] ?? '') === '是') {
      for (const row of rows) {
        if (String(row.id) !== id) {
          row['status'] = '否';
        }
      }
    }
  }
  if (resource === 'config-role' && Array.isArray(values['permissionIds'])) {
    values['permissionIds'] = [...new Set(values['permissionIds'] as readonly string[])];
  }
}
